import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { WorkTypePicker, recordWorkUsage, type PickItem } from "../components/WorkTypePicker";
import { bucketOf, type ActivityTypeWithCategory } from "../components/AddWorkModal";
import { useNavigate } from "react-router-dom";
import { BottomNav } from "../components/BottomNav";
import { useClock } from "../hooks/useClock";
import { useOnlineStatus } from "../hooks/useOnlineStatus";
import type { AuthUser } from "../hooks/useAuth";
import { apiFetch, startAlcoholCheck, COMPANY_ID, isPersonalDevice, isPrivilegedViewer, switchAppVariant, getAccessToken } from "../services/api";
import { batteryPayload } from "../utils/battery";
import { hasFeature } from "../utils/featureFlags";
import { SelfBoardingCard } from "../components/SelfBoardingCard";
import { BoardingRequiredCard } from "../components/BoardingRequiredCard";
import { InstallAppGuide } from "../components/InstallAppGuide";
import { AuthenticatedImage } from "../components/AuthenticatedImage";
import { FaceBadge } from "../components/FaceBadge";
import { enqueue, getQueue } from "../utils/syncQueue";
import { gpsEnqueue, gpsQueuePeek, gpsQueueRemove } from "../utils/gpsQueue";
import { isWebUSBSupported, startAlcoholUSB } from "../services/alcohol-usb";
import { companionPing, startCompanionCheck, type CompanionPoller } from "../services/alcohol-companion";
import { rawToAlcoholMgL, isAlcoholSentinelRaw } from "../services/alcohol-value";
import { getAlcoholThresholds, judgeAlcohol, fmtMgL } from "../utils/alcoholThresholds";
import { PushBell } from "../components/PushBell";
import { PortalSummaryCard } from "../components/PortalSummaryCard";

interface Props { user: AuthUser; onLogout: () => void; }

interface Vessel { id: string; vessel_name: string; long_break_hours?: number; image_path?: string | null; }
interface WorkSession { id: string; vessel_id: string; session_status: string; started_at: string; vessel_name?: string; admin_operated?: number; }
interface ApiResponse<T> { success: boolean; data: T; }

type SessionStatus = "idle" | "working" | "resting";
type ClockOutStep = "none" | "select" | "confirm";
type AlcoholPhase = "idle" | "warming" | "waiting" | "done" | "timeout" | "no_breath";
type WorkSelectAction = "clock_in";

interface WorkTypeItem { id: string; label: string; emoji: string; requires_alcohol?: number; }

/** フォールバック用（API取得失敗時に使用） */
const DEFAULT_vesselWorkTypes: WorkTypeItem[] = [
  { id: "navigation",  label: "航海",      emoji: "🚢", requires_alcohol: 0 },
  { id: "cargo",       label: "荷役",      emoji: "📦", requires_alcohol: 0 },
  { id: "maintenance", label: "点検・整備", emoji: "🔧", requires_alcohol: 0 },
  { id: "watch",       label: "当直",      emoji: "👁", requires_alcohol: 1 },
  { id: "mooring",     label: "係船作業",  emoji: "⚓", requires_alcohol: 0 },
  { id: "cleaning",    label: "清掃",      emoji: "🧹", requires_alcohol: 0 },
  { id: "training",    label: "訓練",      emoji: "📋", requires_alcohol: 0 },
  { id: "other",       label: "その他",    emoji: "📝", requires_alcohol: 0 },
];


const STATUS_CONFIG: Record<SessionStatus, { label: string; color: string; bg: string; dot: string; border: string }> = {
  idle:    { label: "待機中", color: "#64748b", bg: "#f1f5f9", dot: "#94a3b8", border: "#e2e8f0" },
  working: { label: "業務中", color: "#059669", bg: "#ecfdf5", dot: "#10b981", border: "#a7f3d0" },
  resting: { label: "待機中", color: "#64748b", bg: "#f1f5f9", dot: "#94a3b8", border: "#e2e8f0" },
};

const STAMP_LABELS: Record<string, string> = {
  clock_in:  "業務を開始しました",
  clock_out: "業務を終了しました",
};

// ─── JST 日付ユーティリティ ──────────────────────────────────────────────────
/**
 * 指定日時を JST の ISO 文字列（+09:00 オフセット付き）に変換する。
 * `toISOString()` は常に UTC（Z サフィックス）を返すため、
 * JST タイムゾーンが必要な箇所ではこの関数を使う。
 */
function toJstISOString(date: Date = new Date()): string {
  const jst = new Date(date.getTime() + 9 * 60 * 60 * 1000);
  return jst.toISOString().replace('Z', '+09:00');
}

/** JST での YYYY-MM-DD 文字列を返す */
function toJstDateString(date: Date = new Date()): string {
  return toJstISOString(date).slice(0, 10);
}
// ──────────────────────────────────────────────────────────────────────────────

// ─── 臨時作業モーダル ──────────────────────────────────────────────────
interface ActivityMasterItem { id: string; name: string; emoji: string; color: string; }
interface ActivityLogItem { id: string; activity_master_id: string; name: string; emoji: string; color: string; started_at: string; ended_at: string; duration_minutes: number; note?: string; }

function CrewActivityModal({ sessionId, vesselId, onClose }: {
  sessionId: string;
  vesselId: string;
  onClose: () => void;
}) {
  const [activityTypes, setActivityTypes] = useState<ActivityMasterItem[]>([]);
  const [existingLogs, setExistingLogs] = useState<ActivityLogItem[]>([]);
  const [selected, setSelected] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const todayJst = toJstDateString();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [typesRes, logsRes] = await Promise.all([
          apiFetch<any>(`/vessels/${vesselId}/activity-types`),
          apiFetch<any>(`/activity-logs?work_session_id=${sessionId}`),
        ]);
        setActivityTypes(typesRes?.data ?? []);
        setExistingLogs(logsRes?.data ?? []);
      } catch { /* noop */ }
      finally { setLoading(false); }
    };
    load();
  }, [sessionId, vesselId]);

  const handleSave = async () => {
    if (!selected) { setError('作業種別を選択してください'); return; }
    if (!startTime || !endTime) { setError('開始・終了時刻を入力してください'); return; }
    const startIso = `${todayJst}T${startTime}:00+09:00`;
    const endIso   = `${todayJst}T${endTime}:00+09:00`;
    if (new Date(endIso) <= new Date(startIso)) { setError('終了時刻は開始時刻より後にしてください'); return; }
    setSaving(true); setError('');
    try {
      await apiFetch<any>('/activity-logs', {
        method: 'POST',
        body: JSON.stringify({ work_session_id: sessionId, vessel_id: vesselId, activity_master_id: selected, started_at: startIso, ended_at: endIso, note: note || undefined }),
      });
      const res = await apiFetch<any>(`/activity-logs?work_session_id=${sessionId}`);
      setExistingLogs(res?.data ?? []);
      setSelected(''); setStartTime(''); setEndTime(''); setNote('');
    } catch (e: any) { setError(e.message ?? '保存に失敗しました'); }
    finally { setSaving(false); }
  };

  const fmtIso = (iso: string) => {
    try { return new Date(iso).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Tokyo', hour12: false }); } catch { return iso; }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: '20px 20px 0 0', padding: 24, width: '100%', maxWidth: 480, boxShadow: '0 -8px 32px rgba(0,0,0,0.15)', maxHeight: '85dvh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
              {hasFeature('unified_work_types') ? '作業を追加' : '臨時作業申請'}
            </div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>現在のシフトに追加</div>
          </div>
          <button onClick={onClose} style={{ width: 32, height: 32, border: 'none', background: '#f1f5f9', borderRadius: '50%', cursor: 'pointer', fontSize: 16, color: '#64748b' }}>✕</button>
        </div>

        {existingLogs.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8 }}>本日の登録済み</div>
            {existingLogs.map(log => (
              <div key={log.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#f8fafc', borderRadius: 10, marginBottom: 6, border: '1px solid #f1f5f9' }}>
                <span style={{ fontSize: 20 }}>{log.emoji}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{log.name}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>{fmtIso(log.started_at)} → {fmtIso(log.ended_at)}（{log.duration_minutes}分）</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 24, color: '#94a3b8', fontSize: 13 }}>読み込み中...</div>
        ) : activityTypes.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 24, color: '#94a3b8', fontSize: 13 }}>
            {hasFeature('unified_work_types')
              ? 'この船舶に時間を指定して記録する作業が設定されていません。'
              : 'この船舶に特別作業が設定されていません。'}
            <br />管理者に設定を依頼してください。
          </div>
        ) : (
          <>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8 }}>作業を追加</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
              {activityTypes.map((t) => (
                <button key={t.id} onClick={() => setSelected(t.id)}
                  style={{ padding: '12px 8px', borderRadius: 12, border: selected === t.id ? `2px solid ${t.color}` : '2px solid #e2e8f0', background: selected === t.id ? t.color + '18' : '#f8fafc', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, cursor: 'pointer', position: 'relative' }}>
                  {selected === t.id && <div style={{ position: 'absolute', top: 6, right: 6, width: 16, height: 16, borderRadius: '50%', background: t.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4"><polyline points="20 6 9 17 4 12"/></svg></div>}
                  <span style={{ fontSize: 22 }}>{t.emoji}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{t.name}</span>
                </button>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>開始時刻</span>
                <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)}
                  style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 15, outline: 'none', background: '#f8fafc' }} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>終了時刻</span>
                <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)}
                  style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 15, outline: 'none', background: '#f8fafc' }} />
              </label>
            </div>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 14 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>メモ（任意）</span>
              <input type="text" value={note} onChange={e => setNote(e.target.value)} placeholder="例：荷役中の油水分離機確認"
                style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 14, outline: 'none', background: '#f8fafc' }} />
            </label>
            {error && <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 10 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={onClose} style={{ flex: 1, padding: 14, border: '1px solid #e2e8f0', borderRadius: 12, background: '#fff', cursor: 'pointer', fontSize: 14, color: '#475569', fontWeight: 600 }}>閉じる</button>
              <button onClick={handleSave} disabled={saving} style={{ flex: 2, padding: 14, border: 'none', borderRadius: 12, background: saving ? '#93c5fd' : '#7c3aed', color: '#fff', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14 }}>
                {saving ? '登録中...' : '登録する'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export const HomePage: React.FC<Props> = ({ user, onLogout }) => {
  const { time, date } = useClock();
  const isOnline = useOnlineStatus();
  const nav = useNavigate();
  // loc removed: BottomNav handles active tab

  const [sessionStatus, setSessionStatus] = useState<SessionStatus>("idle");
  const [currentSession, setCurrentSession] = useState<WorkSession | null>(null);
  const [vessels, setVessels] = useState<Vessel[]>([]);
  const [selectedVesselId, setSelectedVesselId] = useState<string>("");
  // 本人が乗船中の船（配乗 on_board）。管理者の打刻が「管理者の操作」になるかの表示判定に使う
  const [ownOnBoardVesselId, setOwnOnBoardVesselId] = useState<string>("");
  // 船内端末の船に配乗されていない船員 → 乗船登録をしてからでないと業務開始できない
  const [boardingNeeded, setBoardingNeeded] = useState<{ id: string; name: string } | null>(null);
  const [vesselWorkTypes, setVesselWorkTypes] = useState<WorkTypeItem[]>(DEFAULT_vesselWorkTypes);
  // 特別作業（臨時作業マスタ）。通常業務が無くても、打刻時にこれだけ選んで業務開始できる。
  const [specialTypes, setSpecialTypes] = useState<ActivityTypeWithCategory[]>([]);
  // 長時間離席しきい値（会社共通・マスタのしきい値設定で管理）。オフライン用にキャッシュから初期化
  const [longBreakThresholdHours, setLongBreakThresholdHours] = useState(() => {
    const cached = Number(localStorage.getItem("catms_long_break_hours"));
    return cached > 0 ? cached : 4;
  });
  const [loading, setLoading] = useState(true);
  const [stamping, setStamping] = useState(false);
  const [clockOutStep, setClockOutStep] = useState<ClockOutStep>("none");
  /**
   * 打刻画面の下部メニュー（勤怠確認・ヒヤリハット等）を開いているか。
   *
   * 既定は「閉じる」。この画面でいちばん大事なのは業務開始／終了のボタンで、
   * タイルを常に出していると画面の狭い端末で打刻ボタンが圧迫され、
   * 押し間違いも起きるため（利用者からの指摘）。
   * 開いた状態は端末に覚えるので、よく使う人は開いたままにできる。
   */
  const [homeMenuOpen, setHomeMenuOpen] = useState<boolean>(() => {
    try { return localStorage.getItem("catms_home_menu_open") === "1"; } catch { return false; }
  });
  /** 打刻ボタンが入っている領域。メニューを開いたときにボタンまで送るのに使う */
  const punchAreaRef = useRef<HTMLDivElement>(null);
  /** 業務開始／終了ボタンの枠。「その他の機能」を開いたときにここへスクロールする */
  const mainActionRef = useRef<HTMLDivElement>(null);
  const toggleHomeMenu = () => {
    setHomeMenuOpen(prev => {
      const next = !prev;
      try { localStorage.setItem("catms_home_menu_open", next ? "1" : "0"); } catch { /* 保存できなくても動作に影響しない */ }
      // 開くとタイルの分だけ打刻エリアが狭くなる。以前は下端まで送っていたが、
      // 文字サイズの大きいスマホでは業務開始／終了のボタンが上に押し出されて消えてしまった。
      // いちばん大事なボタンを画面の上に揃えて、ボタンとタイルが一緒に見えるようにする
      // （時計はスクロールで上に隠れてよい）。
      if (next) setTimeout(() => {
        const btn = mainActionRef.current;
        const el = punchAreaRef.current;
        if (btn) btn.scrollIntoView({ block: "start", behavior: "smooth" });
        else if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
      }, 60);
      return next;
    });
  };
  const [selectedWorkTypes, setSelectedWorkTypes] = useState<string[]>([]);
  // 作業開始時の業務選択ステップ（出勤・休憩終了共通）
  const [workSelectStep, setWorkSelectStep] = useState<"none" | "select">("none");
  const [workSelectAction, setWorkSelectAction] = useState<WorkSelectAction>("clock_in");
  const [workSelectTypes, setWorkSelectTypes] = useState<string[]>([]);
  const [workSelectSpecial, setWorkSelectSpecial] = useState<string[]>([]);   // 打刻時に選ぶ特別作業
  const [timeBasedAlcoholRequired, setTimeBasedAlcoholRequired] = useState(false);
  // 陸上管理者・システム管理者・運営管理者代理・代理ログインは「管理者操作」として扱い、
  // 自分の配乗が無くても船を選んで打刻・確認ができる（サーバー側も端末制限を通す実装）。
  const isAdminOperator = useMemo(() => {
    if (["shore_admin", "system_admin"].includes(user.user_role)) return true;
    try {
      const t = getAccessToken();
      if (!t) return false;
      const p = JSON.parse(atob(t.split('.')[1] ?? '')) as { sa_id?: string; imp_by?: string };
      return !!p.sa_id || !!p.imp_by;
    } catch { return false; }
  }, [user.user_role]);
  // 船員デモ（管理者の代理ログイン）・運営の代理。打刻はすべて「管理者の操作」になる
  const isImpersonated = useMemo(() => {
    try {
      const t = getAccessToken();
      if (!t) return false;
      const p = JSON.parse(atob(t.split('.')[1] ?? '')) as { sa_id?: string; imp_by?: string };
      return !!p.sa_id || !!p.imp_by;
    } catch { return false; }
  }, [user.user_id]);
  // 管理者が選んだ船（端末に覚えておき、次に開いたときも同じ船から始められる）
  const [adminVessels, setAdminVessels] = useState<Vessel[]>([]);
  // 運営管理者アカウントは会社のユーザー表に存在しない合成IDなので打刻できない。
  // 船を選んで各画面を確認するための入口として扱う。
  const isOperatorViewOnly = useMemo(() => {
    try { return localStorage.getItem("catms_is_super_admin") === '1'; } catch { return false; }
  }, []);
  // この打刻でアルコール検査が必須か（画面に「測定せず打刻」を出すかの判定に使う）
  const [alcoholRequiredNow, setAlcoholRequiredNow] = useState(false);
  // 保留中の打刻アクション（startAlcohol/doStamp で参照するため ref も持つ）
  const pendingActionRef = useRef<WorkSelectAction>("clock_in");
  const pendingAlcoholRequiredRef = useRef<boolean>(false); // カメラ後にアルコール検査へ進むか
  const pendingWorkTypesRef = useRef<string[]>([]);
  const [toast, setToast] = useState<{ msg: string; type: "success" | "error" } | null>(null);
  const [alcoholPhase, setAlcoholPhase] = useState<AlcoholPhase>("idle");
  const [alcoholValue, setAlcoholValue] = useState<number | null>(null);
  const [warmupCountdown, setWarmupCountdown] = useState<number>(10);
  const warmupMaxRef = useRef<number>(10);        // ウォームアップの最大秒数（初回値を保持）
  const waitingStartAtRef = useRef<number | null>(null); // 呼気待機フェーズの開始時刻
  const MIN_BREATH_MS = 5000;          // 有効な呼気とみなす最低持続時間(ms) — 5秒
  /** 吹き込みの残り秒数（crew_breath_countdown の会社のみ表示。null=非表示） */
  const [breathLeft, setBreathLeft] = useState<number | null>(null);
  // 0=非表示  1=結果表示中(2秒)  2=次の人ボタン表示
  const [clockInDoneStep, setClockInDoneStep] = useState<0 | 1 | 2>(0);
  const [clockInDoneAlcohol, setClockInDoneAlcohol] = useState<number | null>(null);
  /** 完了オーバーレイの種別（in=業務開始 / out=業務終了。終了は crew_session_guard の会社のみ） */
  const [doneKind, setDoneKind] = useState<"in" | "out">("in");
  interface NoticeItem { id: string; content: string; sender_name: string; created_at: string; }
  const [noticePopup, setNoticePopup] = useState<NoticeItem[] | null>(null);
  const [usbDebugLog, setUsbDebugLog] = useState<string[]>([]);
  const [breathRetry, setBreathRetry] = useState(0); // 呼気未検知による再測定回数
  const breathRetryRef = useRef(0); // コールバック内でも最新値を参照するためのref
  const [deviceToken, setDeviceToken] = useState<string | null>(null);
  const [missingPosition, setMissingPosition] = useState(false); // 乗船中だが配乗の役職が空
  const [, setPermTick] = useState(0); // 権限が更新されたら再描画（メニューは localStorage の権限を描画時に読む）
  useEffect(() => {
    const h = () => setPermTick(t => t + 1);
    window.addEventListener("catms-permissions-updated", h);
    return () => window.removeEventListener("catms-permissions-updated", h);
  }, []);
  const [_deviceVesselName, setDeviceVesselName] = useState<string | null>(null); // set only (kept for future use)
  const [pendingCount, setPendingCount] = useState(0);
  const [manualAlcoInput, setManualAlcoInput] = useState(false);
  // 検知器のコールバックは古い state を見てしまうため、手入力中かどうかは ref でも保持する
  const manualAlcoInputRef = useRef(false);
  const [manualAlcoValue, setManualAlcoValue] = useState('');
  const [activityModalOpen, setActivityModalOpen] = useState(false);
  // カメラ（出勤前に本人確認撮影）
  type CameraStep = 'idle' | 'preview' | 'captured';
  const [cameraStep, setCameraStep] = useState<CameraStep>('idle');
  const [capturedPhoto, setCapturedPhoto] = useState<string | null>(null);
  const capturedPhotoRef = useRef<string | null>(null);
  // アルコール測定中(息吹き中)に撮る本人確認写真。測定必須時に doStamp へ添付する
  const alcoholPhotoRef = useRef<string | null>(null);
  // captureSnapshot は下方で定義するため、startAlcohol からは ref 経由で呼ぶ（宣言順の回避）
  const captureSnapshotRef = useRef<(() => Promise<string | undefined>) | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const esRef = useRef<EventSource | CompanionPoller | null>(null);
  const usbCleanupRef = useRef<(() => void) | null>(null);

  const showToast = (msg: string, type: "success" | "error" = "success") => {
    setToast({ msg, type });
    // エラーは10秒、成功は3秒表示
    setTimeout(() => setToast(null), type === "error" ? 10000 : 3000);
  };

  const loadInitialData = useCallback(async () => {
    setLoading(true);
    try {
      const dToken = localStorage.getItem("catms_device_token");
      const dVesselId = localStorage.getItem("catms_device_vessel_id");
      const dVesselName = localStorage.getItem("catms_device_vessel_name");
      setDeviceToken(dToken);
      setDeviceVesselName(dVesselName);

      const [sessionRes, assignRes] = await Promise.all([
        apiFetch<ApiResponse<WorkSession | null>>("/timestamps/sessions/active/current"),
        apiFetch<{ success: boolean; data: { user_id: string; vessel_id: string; vessel_name: string; vessel_image_path?: string | null; assignment_status: string; position?: string | null }[] }>("/vessels/assignments/list"),
      ]);

      // 乗船中なのに配乗の役職が空 → 役職に付いた機能が使えないので本人に知らせる（分岐に関係なく判定）
      const ownOnBoard = (assignRes.data ?? []).find(a => a.user_id === user.user_id && a.assignment_status === "on_board");
      setMissingPosition(!!ownOnBoard && !String(ownOnBoard.position ?? "").trim());
      setOwnOnBoardVesselId(ownOnBoard?.vessel_id ?? "");
      setBoardingNeeded(null);
      // 船舶ID→画像パスのマップ（稼働中セッション/デバイス固定の分岐でも船画像を出すため）
      const vesselImgById = new Map<string, string | null>();
      for (const a of (assignRes.data ?? [])) {
        if (a.vessel_id && a.vessel_image_path && !vesselImgById.has(a.vessel_id)) {
          vesselImgById.set(a.vessel_id, a.vessel_image_path);
        }
      }

      if (sessionRes.data) {
        const s = sessionRes.data;
        setCurrentSession(s);
        const status: SessionStatus = s.session_status === "completed" ? "idle" : "working";
        setSessionStatus(status);
        localStorage.setItem("catms_session_status", status);
        localStorage.setItem("catms_work_session_id", s.id);
        // この勤務中表示が誰のものかを残す（共有端末で次の人に出さないための照合用）
        localStorage.setItem("catms_session_user", user.user_id);
        // アクティブセッションの船舶を使用
        const vesselId = s.vessel_id;
        const vesselName = s.vessel_name ?? "";
        setVessels([{ id: vesselId, vessel_name: vesselName, image_path: vesselImgById.get(vesselId) ?? null }]);
        setSelectedVesselId(vesselId);
        localStorage.setItem("catms_last_vessel_id", vesselId);
        localStorage.setItem("catms_last_vessel_name", vesselName);
      } else {
        setSessionStatus("idle");
        localStorage.setItem("catms_session_status", "idle");
        localStorage.removeItem("catms_work_session_id");
        localStorage.removeItem("catms_session_user");

        if (dToken && dVesselId) {
          // デバイストークンで固定された船舶
          setVessels([{ id: dVesselId, vessel_name: dVesselName ?? "", image_path: vesselImgById.get(dVesselId) ?? null }]);
          setSelectedVesselId(dVesselId);
          localStorage.setItem("catms_last_vessel_id", dVesselId);
          // この船に配乗されていない船員は、端末の船に黙って乗船扱いにせず、先に乗船登録をしてもらう。
          // 管理者（船員デモ含む）は登録なしで打刻でき、その記録は「管理者の操作」として集計から外れる
          if (!isAdminOperator && ownOnBoard?.vessel_id !== dVesselId) {
            setBoardingNeeded({ id: dVesselId, name: dVesselName ?? "" });
          }
        } else if (isAdminOperator) {
          // 管理者は配乗の有無に関わらず、入れる船から選べるようにする
          const sel = await apiFetch<{ data?: { admin_operator: boolean; vessels: { id: string; vessel_name: string; image_path: string | null }[] } }>("/vessels/selectable");
          const list = (sel?.data?.vessels ?? []).map(v => ({ id: v.id, vessel_name: v.vessel_name, image_path: v.image_path }));
          setVessels(list);
          setAdminVessels(list);
          const remembered = localStorage.getItem("catms_admin_vessel_id");
          const own = (assignRes.data ?? []).find(a => a.user_id === user.user_id && a.assignment_status === "on_board");
          const pick = list.find(v => v.id === remembered)
            ?? list.find(v => v.id === own?.vessel_id)
            ?? (list.length === 1 ? list[0] : undefined);
          setSelectedVesselId(pick?.id ?? "");
          if (pick) {
            localStorage.setItem("catms_last_vessel_id", pick.id);
            localStorage.setItem("catms_last_vessel_name", pick.vessel_name);
          }
        } else {
          // 管理側の乗組員配置（on_board）から取得
          const assignment = (assignRes.data ?? []).find(
            a => a.user_id === user.user_id && a.assignment_status === "on_board"
          );
          if (assignment) {
            setVessels([{ id: assignment.vessel_id, vessel_name: assignment.vessel_name, image_path: assignment.vessel_image_path ?? null }]);
            setSelectedVesselId(assignment.vessel_id);
            localStorage.setItem("catms_last_vessel_id", assignment.vessel_id);
            localStorage.setItem("catms_last_vessel_name", assignment.vessel_name);
          } else {
            setVessels([]);
            setSelectedVesselId("");
          }
        }
      }
    } catch {
      // オフライン時はlocalStorageから復元。「勤務中」の表示は誰のものかを照合し、
      // 共有端末で前の人の勤務中が次の人に出ないようにする（crew_session_guard の会社のみ）
      const storedStatus = localStorage.getItem("catms_session_status") as SessionStatus | null;
      const sessUser = localStorage.getItem("catms_session_user");
      const isForeign = hasFeature("crew_session_guard") && storedStatus !== null && storedStatus !== "idle"
        && !!sessUser && sessUser !== user.user_id;
      if (storedStatus && !isForeign) setSessionStatus(storedStatus);
      // 未送信キューに自分の「業務開始」が残っていれば勤務中を復元する
      // （圏外で 開始→ログアウト→別の人が使う→自分が再ログイン→終了 の流れを通すため）
      if (hasFeature("crew_session_guard")) {
        const mine = getQueue().filter(q => q.user_id === user.user_id);
        const lastMine = mine[mine.length - 1];
        if ((isForeign || !storedStatus || storedStatus === "idle") && lastMine && lastMine.timestamp_type === "clock_in") {
          setSessionStatus("working");
          setCurrentSession({
            id: lastMine.session_id, vessel_id: lastMine.vessel_id ?? "",
            session_status: "working", started_at: lastMine.recorded_at,
            vessel_name: localStorage.getItem("catms_last_vessel_name") ?? undefined,
          });
          localStorage.setItem("catms_session_status", "working");
          localStorage.setItem("catms_work_session_id", lastMine.session_id);
          localStorage.setItem("catms_session_user", user.user_id);
        }
      }

      const dToken = localStorage.getItem("catms_device_token");
      const dVesselId = localStorage.getItem("catms_device_vessel_id");
      const dVesselName = localStorage.getItem("catms_device_vessel_name");
      setDeviceToken(dToken);
      setDeviceVesselName(dVesselName);

      const cachedVesselId = localStorage.getItem("catms_last_vessel_id");
      const cachedVesselName = localStorage.getItem("catms_last_vessel_name");
      if (dToken && dVesselId && dVesselName) {
        setVessels([{ id: dVesselId, vessel_name: dVesselName }]);
        setSelectedVesselId(dVesselId);
      } else if (cachedVesselId && cachedVesselName) {
        setVessels([{ id: cachedVesselId, vessel_name: cachedVesselName }]);
        setSelectedVesselId(cachedVesselId);
      } else {
        setVessels([]);
        setSelectedVesselId("");
      }
    } finally {
      setLoading(false);
    }
  }, [user.user_id]);

  useEffect(() => { loadInitialData(); }, [loadInitialData]);

  // 長時間離席しきい値（会社共通）をマスタのしきい値設定から取得。失敗時はキャッシュ/既定値のまま
  useEffect(() => {
    apiFetch<{ success: boolean; data: { key: string; value: number }[] }>(`/attendance-thresholds`)
      .then(res => {
        const item = (res?.data ?? []).find(t => t.key === "long_break_hours");
        if (item && typeof item.value === "number" && item.value > 0) {
          setLongBreakThresholdHours(item.value);
          localStorage.setItem("catms_long_break_hours", String(item.value));
        }
      })
      .catch(() => { /* オフライン時はキャッシュ/既定値を使用 */ });
  }, []);

  // 船舶が選択されたら業務項目を更新（同じ船に対しては1回だけ取得する）
  const lastWorkTypesVesselRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedVesselId) return;
    if (lastWorkTypesVesselRef.current === selectedVesselId) return;
    lastWorkTypesVesselRef.current = selectedVesselId;
    apiFetch<{ success: boolean; data: { id: string; name: string; emoji: string; requires_alcohol: number }[] }>(
      `/vessels/${selectedVesselId}/work-types`
    ).then(res => {
      const types = res?.data ?? [];
      if (types.length > 0) {
        setVesselWorkTypes(types.map(t => ({ id: t.id, label: t.name, emoji: t.emoji, requires_alcohol: t.requires_alcohol })));
        localStorage.setItem(`catms_work_types_${selectedVesselId}`, JSON.stringify(types));
      } else {
        setVesselWorkTypes(DEFAULT_vesselWorkTypes);
      }
    }).catch(() => {
      try {
        const cached = localStorage.getItem(`catms_work_types_${selectedVesselId}`);
        if (cached) {
          const types = JSON.parse(cached) as { id: string; name: string; emoji: string; requires_alcohol: number }[];
          if (types.length > 0) {
            setVesselWorkTypes(types.map(t => ({ id: t.id, label: t.name, emoji: t.emoji, requires_alcohol: t.requires_alcohol })));
            return;
          }
        }
      } catch { /* ignore */ }
      setVesselWorkTypes(DEFAULT_vesselWorkTypes);
    });
  }, [selectedVesselId, vessels]);

  // 特別作業（臨時作業マスタ）を取得。打刻の業務選択画面で「特別作業だけ」でも選べるようにする。
  useEffect(() => {
    if (!selectedVesselId) { setSpecialTypes([]); return; }
    apiFetch<{ data: ActivityTypeWithCategory[] }>(`/vessels/${selectedVesselId}/activity-types`)
      .then(r => setSpecialTypes(r?.data ?? []))
      .catch(() => setSpecialTypes([]));
  }, [selectedVesselId]);

  // オンライン復帰時: データ再取得（初回マウント時は上の effect が取得済みなので二重に呼ばない）
  const onlineEffectArmed = useRef(false);
  useEffect(() => {
    if (!isOnline) { onlineEffectArmed.current = true; return; }
    if (!onlineEffectArmed.current) return; // 初回（オンラインのままマウント）はスキップ
    loadInitialData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline]);

  // 60秒ごとにサーバーのセッション状態を確認（管理側で削除された場合も検知）
  useEffect(() => {
    const checkSession = async () => {
      if (!isOnline) return;
      try {
        // バックグラウンドの状態確認: 401でも打刻中にログイン画面へ飛ばさない
        const r = await apiFetch<ApiResponse<WorkSession | null>>("/timestamps/sessions/active/current", {}, { noRedirect: true });
        if (r.data) {
          // サーバーにセッションあり → 最新状態に更新
          setCurrentSession(r.data);
          const status: SessionStatus = r.data.session_status === "completed" ? "idle" : "working";
          setSessionStatus(status);
          localStorage.setItem("catms_session_status", status);
          localStorage.setItem("catms_work_session_id", r.data.id);
          localStorage.setItem("catms_session_user", user.user_id);
        } else {
          // サーバーにセッションなし → ローカルが「勤務中」ならリセット
          if (sessionStatus !== "idle") {
            setCurrentSession(null);
            setSessionStatus("idle");
            localStorage.setItem("catms_session_status", "idle");
            localStorage.removeItem("catms_work_session_id");
            localStorage.removeItem("catms_session_user");
            showToast("⚠️ 勤怠データがサーバーと一致しません。状態をリセットしました。", "error");
          }
        }
      } catch { /* オフライン時は無視 */ }
    };

    const timer = setInterval(checkSession, 60_000);
    return () => clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline, sessionStatus]);

  // カメラ停止（useCallback: アンマウント useEffect で安定参照が必要）
  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  }, []);
  useEffect(() => () => stopCamera(), [stopCamera]);

  // オフラインキュー件数を定期チェック
  useEffect(() => {
    const check = () => setPendingCount(getQueue().length);
    check();
    const id = setInterval(check, 5000);
    return () => clearInterval(id);
  }, []);

  // デバイスハートビート: 起動時 + 5分ごと（デバイス管理用）
  useEffect(() => {
    const sendBeat = () => {
      const dToken = localStorage.getItem("catms_device_token");
      if (!dToken || !isOnline) return;
      const doSend = async (lat?: number, lon?: number) => {
        // 充電残量も一緒に送る（船内の共有端末が電池切れで使えなくなるのを陸上から気づけるように）
        const bat = await batteryPayload();
        apiFetch("/devices/heartbeat", {
          method: "POST",
          body: JSON.stringify({ device_token: dToken, company_id: COMPANY_ID, latitude: lat, longitude: lon, ...bat }),
        }, { noRedirect: true }).catch((e: Error) => {
          if (e.message.includes('デバイスが見つかりません')) {
            localStorage.removeItem("catms_device_token");
            localStorage.removeItem("catms_device_vessel_id");
            localStorage.removeItem("catms_device_vessel_name");
          }
        });
      };
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          pos => doSend(pos.coords.latitude, pos.coords.longitude),
          () => doSend(),
          { timeout: 8000, maximumAge: 60000 }
        );
      } else {
        doSend();
      }
    };
    sendBeat();
    const id = setInterval(sendBeat, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [isOnline]);

  // Wake Lock: デバイストークンが登録されているとき、画面を常にオン（GPSが途切れないよう）
  useEffect(() => {
    if (!deviceToken) return;
    if (!('wakeLock' in navigator)) return;
    let wakeLock: WakeLockSentinel | null = null;
    const request = async () => {
      try { wakeLock = await (navigator as unknown as { wakeLock: { request: (type: string) => Promise<WakeLockSentinel> } }).wakeLock.request('screen'); } catch { /* ignore */ }
    };
    request();
    const onVisibilityChange = () => { if (document.visibilityState === 'visible') request(); };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      wakeLock?.release().catch(() => {});
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [deviceToken]);

  // GPS位置ログ: 1分ごとに /gps/location へ送信（管理画面の航跡表示用）
  // 出勤中・休憩中のみ記録（非番時はオフ）。
  // gps_offline_buffer の会社は、圏外のあいだも測位して端末に貯め（約5分おき・最大約3日分）、
  // 電波が戻ったときに /gps/location/bulk でまとめて送って航跡を後から埋める。
  useEffect(() => {
    const buffering = hasFeature("gps_offline_buffer");
    if (!isOnline && !buffering) return; // 従来挙動: 圏外中は何もしない
    if (sessionStatus === "idle") return; // 出勤していない間は送らない

    const vesselId = currentSession?.vessel_id || selectedVesselId || undefined;

    const flushQueue = () => {
      const pts = gpsQueuePeek(300);
      if (pts.length === 0) return;
      apiFetch("/gps/location/bulk", {
        method: "POST", body: JSON.stringify({ points: pts }),
      }, { noRedirect: true }).then(() => gpsQueueRemove(pts.length)).catch(() => {});
    };

    const sendGps = () => {
      if (!navigator.geolocation) return;
      navigator.geolocation.getCurrentPosition(
        pos => {
          const point = {
            latitude:  pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy:  pos.coords.accuracy  ?? undefined,
            speed:     pos.coords.speed     ?? undefined,
            heading:   pos.coords.heading   ?? undefined,
            vessel_id: vesselId,
            recorded_at: new Date().toISOString(),
          };
          if (!navigator.onLine && buffering) { gpsEnqueue(point); return; } // 圏外: 貯めるだけ
          apiFetch("/gps/location", {
            method: "POST", body: JSON.stringify(point),
          }, { noRedirect: true })
            .then(() => { if (buffering) flushQueue(); })           // 送れた＝電波あり。貯め分も流す
            .catch(() => { if (buffering) gpsEnqueue(point); });    // 送信失敗は貯めて後で送る
        },
        () => {}, // GPS取得失敗は無視
        { timeout: 10000, maximumAge: 30000, enableHighAccuracy: false }
      );
    };

    sendGps(); // 初回即時
    const id = setInterval(sendGps, 60_000); // 1分ごと
    return () => clearInterval(id);
  }, [isOnline, sessionStatus, currentSession?.vessel_id, selectedVesselId]);

  // EventSource のクリーンアップ
  useEffect(() => {
    return () => { esRef.current?.close(); };
  }, []);

  // 出勤完了: 2.5秒後に step1 → step2
  useEffect(() => {
    if (clockInDoneStep !== 1) return;
    const t = setTimeout(() => setClockInDoneStep(2), 2500);
    return () => clearTimeout(t);
  }, [clockInDoneStep]);

  // 吹き込みの残り秒数ガイド（crew_breath_countdown の会社のみ）。
  // 画面は「2〜3秒」と案内していたが、内部では呼気待機から5秒未満（MIN_BREATH_MS）に届いた結果を
  // 「呼気未検知」として捨てるため、案内どおりだと検知しない。残り秒数を見せて0まで吹いてもらう。
  useEffect(() => {
    if (alcoholPhase !== "waiting" || !hasFeature("crew_breath_countdown")) { setBreathLeft(null); return; }
    if (waitingStartAtRef.current === null) waitingStartAtRef.current = Date.now();
    const tick = () => {
      const start = waitingStartAtRef.current ?? Date.now();
      setBreathLeft(Math.max(0, Math.ceil((MIN_BREATH_MS - (Date.now() - start)) / 1000)));
    };
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alcoholPhase]);

  const doStamp = useCallback(async (action: string, workTypes?: string[], alcoValue?: number, faceImage?: string, alcoholManual?: boolean, alcoholImage?: string) => {
    setStamping(true);
    try {
      if (action === "clock_in" && !selectedVesselId) throw new Error("船舶を選択してください");

      // GPS取得（開始・終了とも記録する。取得失敗しても打刻は続行）
      let gpsLat: number | undefined;
      let gpsLon: number | undefined;
      if (navigator.geolocation) {
        try {
          const pos = await new Promise<GeolocationPosition>((resolve, reject) =>
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 8000, maximumAge: 30000 })
          );
          gpsLat = pos.coords.latitude;
          gpsLon = pos.coords.longitude;
        } catch { /* GPS不可の場合は無視して続行 */ }
      }

      const recordedAt = new Date().toISOString();
      const note = workTypes && workTypes.length > 0 ? workTypes.join(",")
                 : alcoValue !== undefined ? `アルコールチェック: ${alcoValue.toFixed(3)} mg/L`
                 : undefined;

      // セッションID（clock_inは新規生成、それ以外は既存）
      const localSessionId = action === "clock_in"
        ? crypto.randomUUID()
        : (currentSession?.id ?? localStorage.getItem("catms_work_session_id") ?? "");
      if (action !== "clock_in" && !localSessionId) throw new Error("セッションが見つかりません。再ログインしてください。");

      // ── オンライン: 即時送信 ──
      try {
        const body: Record<string, unknown> = {
          timestamp_type: action,
          recorded_at: recordedAt,
          session_id: localSessionId,
        };
        if (action === "clock_in") { body.vessel_id = selectedVesselId; }
        else { body.work_session_id = localSessionId; }
        if (gpsLat !== undefined) { body.latitude = gpsLat; body.longitude = gpsLon; }
        if (note) body.note = note;
        // work_type: ID → ラベル（名前）に変換してから送信
        if (workTypes && workTypes.length > 0) {
          const labels = workTypes.map(id => vesselWorkTypes.find(w => w.id === id)?.label ?? specialTypes.find(s => s.id === id)?.name ?? id);
          body.work_type = labels.join(",");
        }
        if (alcoValue !== undefined) body.alcohol_value = alcoValue;
        if (alcoholManual) body.alcohol_manual = true;
        if (faceImage && action === 'clock_in') body.face_image = faceImage;
        if (alcoholImage && action === 'clock_in') body.alcohol_image = alcoholImage;
        body.auth_method = faceImage ? 'face_photo' : 'id_password';

        // noRedirect: 401時もここでリダイレクトさせず、下の catch で打刻をローカル保存してから誘導する
        const res = await apiFetch<ApiResponse<{ work_session: WorkSession }>>("/timestamps", {
          method: "POST", body: JSON.stringify(body),
        }, { noRedirect: true });

        // サーバー応答でUI更新（オンライン時の正常系）
        const ws = res.data.work_session;
        setCurrentSession(ws);
        const nextStatus: SessionStatus =
          ws.session_status === "completed" ? "idle" :
          ws.session_status === "completed" ? "idle" : "working";
        setSessionStatus(nextStatus);
        localStorage.setItem("catms_session_status", nextStatus);
        if (nextStatus === "idle") {
          localStorage.removeItem("catms_work_session_id");
          localStorage.removeItem("catms_session_user");
          setCurrentSession(null);
        } else {
          localStorage.setItem("catms_work_session_id", ws.id);
          localStorage.setItem("catms_session_user", user.user_id);
        }
        if (action === "clock_in") {
          const v = vessels.find(v => v.id === selectedVesselId);
          if (v) localStorage.setItem("catms_last_vessel_name", v.vessel_name);
          const dToken = localStorage.getItem("catms_device_token");
          if (dToken) {
            apiFetch("/devices/heartbeat", {
              method: "POST",
              body: JSON.stringify({ device_token: dToken, company_id: COMPANY_ID, latitude: gpsLat, longitude: gpsLon }),
            }, { noRedirect: true }).catch((e: Error) => {
              if (e.message.includes('デバイスが見つかりません')) {
                localStorage.removeItem("catms_device_token");
                localStorage.removeItem("catms_device_vessel_id");
                localStorage.removeItem("catms_device_vessel_name");
              }
            });
          }
        }

      } catch (netErr: unknown) {
        // ── オフライン/通信エラー or 認証切れ: キューに保存して打刻を失わない ──
        const isNetworkError = netErr instanceof TypeError || (netErr instanceof Error && netErr.message.includes("fetch"));
        const isAuthError = netErr instanceof Error &&
          (netErr.message.includes("再ログイン") || netErr.message.includes("認証") || netErr.message.includes("トークン"));
        if (!isNetworkError && !isAuthError) throw netErr; // サーバーからの業務系400/500エラーは再スロー

        // 打刻内容をローカルキューへ保存（オンライン復帰・再ログイン後に自動送信）
        enqueue({
          session_id: localSessionId,
          timestamp_type: action,
          vessel_id: action === "clock_in" ? selectedVesselId : undefined,
          recorded_at: recordedAt,
          alcohol_value: alcoValue,
          alcohol_manual: alcoholManual,
          // オフライン出勤の本人確認写真も端末に保持し、オンライン復帰時に送信→削除する
          face_image: action === 'clock_in' ? faceImage : undefined,
          latitude: gpsLat,
          longitude: gpsLon,
          note,
          work_type: workTypes && workTypes.length > 0
            ? workTypes.map(id => vesselWorkTypes.find(w => w.id === id)?.label ?? specialTypes.find(s => s.id === id)?.name ?? id).join(",")
            : undefined,
          user_id: user.user_id,   // 共有端末で本人のトークンで同期するため
        });

        if (isAuthError) {
          // セッション切れ: 打刻はローカル保存済み。再ログイン後に自動送信されるため打刻自体は失われない。
          showToast("セッションの有効期限が切れました。打刻は保存済みです。再ログイン後に自動送信されます。", "error");
          setTimeout(() => window.location.replace('/login'), 1500);
          return;
        }

        // 楽観的UIでステータスを先に更新（オフライン時）
        const nextStatus: SessionStatus =
          action === "clock_in" ? "working" : "idle";
        if (action === "clock_in") {
          const v = vessels.find(v => v.id === selectedVesselId);
          setCurrentSession({ id: localSessionId, vessel_id: selectedVesselId, session_status: "working", started_at: recordedAt, vessel_name: v?.vessel_name });
          localStorage.setItem("catms_work_session_id", localSessionId);
          localStorage.setItem("catms_session_user", user.user_id);
          if (v) localStorage.setItem("catms_last_vessel_name", v.vessel_name);
        } else if (nextStatus === "idle") {
          setCurrentSession(null);
          localStorage.removeItem("catms_work_session_id");
          localStorage.removeItem("catms_session_user");
        }
        setSessionStatus(nextStatus);
        localStorage.setItem("catms_session_status", nextStatus);
        showToast("📴 オフライン保存しました（後で自動送信）", "success");
      }

      setClockOutStep("none");
      setSelectedWorkTypes([]);
      // 時刻をlocalStorageに保存（長時間後の業務開始でアルコール必須判定に使用）
      if (action === "clock_out") localStorage.setItem("catms_last_work_end_at", new Date().toISOString());
      if (action === "clock_in") {
        setDoneKind("in");
        setClockInDoneAlcohol(alcoValue ?? null);
        setClockInDoneStep(1);
        setAlcoholPhase("idle");
        // 未読お知らせを取得してポップアップ表示
        apiFetch<{ data: NoticeItem[] }>("/messages/notices")
          .then(r => { if (r.data?.length > 0) setNoticePopup(r.data); })
          .catch(() => {});
      } else if (action === "clock_out" && hasFeature("crew_session_guard") && !isPersonalDevice()) {
        // 共有端末（crew_session_guard の会社）: 業務終了も開始と同じ完了画面を出し、
        // 「次の方はこちら」でログイン画面へ戻れるようにする（画面の出しっぱなし防止）
        setDoneKind("out");
        setClockInDoneAlcohol(alcoValue ?? null);
        setClockInDoneStep(1);
        setAlcoholPhase("idle");
      } else {
        showToast(STAMP_LABELS[action] ?? "完了", "success");
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "エラーが発生しました";
      console.error('[doStamp] 打刻エラー:', err, { action, alcoValue, selectedVesselId });
      showToast(`❌ ${msg}`, "error");
    } finally {
      setStamping(false);
    }
  }, [currentSession, selectedVesselId, vessels, vesselWorkTypes, specialTypes]);

  const startAlcohol = useCallback(async () => {
    console.log('[startAlcohol] 開始, vessel:', selectedVesselId);
    setAlcoholPhase("warming");
    setAlcoholValue(null);
    alcoholPhotoRef.current = null;
    setWarmupCountdown(10);
    warmupMaxRef.current = 10;
    waitingStartAtRef.current = null;
    setUsbDebugLog([]);
    setBreathRetry(0);
    breathRetryRef.current = 0;
    esRef.current?.close();
    esRef.current = null;

    // 息吹き中の本人確認写真を裏で1枚撮る（測定必須のときの記録用）。撮れなくても打刻は継続。
    const grabAlcoholPhoto = () => {
      if (alcoholPhotoRef.current) return;
      captureSnapshotRef.current?.().then(u => { if (u && !alcoholPhotoRef.current) alcoholPhotoRef.current = u; });
    };

    // 測定値確定時の共通処理（capturedPhotoRef は呼び出し時点の値を使用）
    // 危険閾値を超えた場合もサーバーに必ず送信し、管理者への警告アラートを生成する。
    // 引数は検知器が返す生バイト（1 = 0.001 mg/L）。コンパニオン／WebUSB／PCバックエンドの
    // どの経路でも生バイトが届くので、mg/L への変換と低濃度域の丸めはここに集約する。
    const handleRawValue = (raw: number) => {
      // 最終安全弁: 番兵値（呼気未検知コード 0xEE/0xAA）はどの経路から届いても結果にしない。
      // コンパニオン経路は手前でも弾いているが、WebUSB等の経路や将来の変更に備えてここでも守る
      if (isAlcoholSentinelRaw(raw)) {
        const next = breathRetryRef.current + 1;
        breathRetryRef.current = next;
        setBreathRetry(next);
        setAlcoholPhase("no_breath");
        esRef.current?.close(); esRef.current = null;
        usbCleanupRef.current?.(); usbCleanupRef.current = null;
        return;
      }
      const mgL = rawToAlcoholMgL(raw);
      console.log('[startAlcohol] 測定値:', raw, '(raw) →', mgL, 'mg/L, 写真:', capturedPhotoRef.current ? 'あり' : 'なし', 'アルコール写真:', alcoholPhotoRef.current ? 'あり' : 'なし');
      setAlcoholValue(mgL);
      setAlcoholPhase("done");
      // 法定基準値超過時も含めて常にサーバーへ送信（audit記録・アラート生成のため）
      // 第6引数: アルコール測定中に撮った本人確認写真
      doStamp(pendingActionRef.current, pendingWorkTypesRef.current.length > 0 ? pendingWorkTypesRef.current : undefined, mgL, capturedPhotoRef.current ?? undefined, false, alcoholPhotoRef.current ?? undefined);
    };

    // ① Androidコンパニオンアプリ（localhost:7777）— 最優先
    const ping = await companionPing();
    setUsbDebugLog([`🔎 Companion確認: ${ping.detail}`]);
    const companionReady = ping.ok;
    if (companionReady) {
      setUsbDebugLog(prev => [...prev, "📱 コンパニオンアプリ経由で測定開始"]);
      esRef.current = startCompanionCheck(
        (countdown) => {
          if (countdown > warmupMaxRef.current) warmupMaxRef.current = countdown;
          setAlcoholPhase("warming"); setWarmupCountdown(countdown);
        },
        () => {
          if (waitingStartAtRef.current === null) waitingStartAtRef.current = Date.now();
          setAlcoholPhase("waiting");
          grabAlcoholPhoto(); // 息吹き中に本人確認写真を撮る
        },
        (value) => {
          const isSentinel = (value === 0xAA || value === 0xEE);
          const elapsed = waitingStartAtRef.current ? Date.now() - waitingStartAtRef.current : 0;
          console.log('[companion] value:', value, 'sentinel:', isSentinel, 'elapsed:', elapsed);
          if (isSentinel || !waitingStartAtRef.current || elapsed < MIN_BREATH_MS) {
            const next = breathRetryRef.current + 1;
            breathRetryRef.current = next;
            setBreathRetry(next);
            setAlcoholPhase("no_breath");
            esRef.current?.close(); esRef.current = null;
            return;
          }
          handleRawValue(value);
        },
        () => {
          if (breathRetryRef.current > 0) setAlcoholPhase("no_breath");
          else setAlcoholPhase("timeout");
        },
        (msg) => { setUsbDebugLog(prev => [...prev, `ERR: ${msg}`]); setAlcoholPhase("timeout"); },
        (hex) => {
          setUsbDebugLog(prev => [...prev.slice(-18), `RAW: ${hex}`]);
          if (hex.includes("呼気未検知")) {
            const next = breathRetryRef.current + 1;
            breathRetryRef.current = next;
            setBreathRetry(next);
            setAlcoholPhase("no_breath");
            esRef.current?.close(); esRef.current = null;
          }
        },
      );
      return;
    }

    // ② WebUSB（Android Chromeはカーネルドライバ競合で通常失敗）
    if (isWebUSBSupported()) {
      startAlcoholUSB((ev) => {
        if (ev.type === "warming") { setAlcoholPhase("warming"); setWarmupCountdown(ev.countdown); }
        if (ev.type === "waiting") { setAlcoholPhase("waiting"); grabAlcoholPhoto(); }
        if (ev.type === "done")    { handleRawValue(ev.value); }
        if (ev.type === "debug")   { setUsbDebugLog(prev => [...prev, ev.message]); }
        if (ev.type === "error")   {
          // 手入力を開いたあとに届く中断通知で画面を閉じない
          if (ev.message === 'cancelled') { if (!manualAlcoInputRef.current) setAlcoholPhase("idle"); }
          else { setUsbDebugLog(prev => [...prev, `ERR: ${ev.message}`]); setAlcoholPhase("timeout"); }
        }
      }).then(cancel => { usbCleanupRef.current = cancel; });
      return;
    }

    // ③ PCバックエンド SSE（ngrok 経由）
    // チケット取得失敗時（JWT をURL露出させないため）はタイムアウト扱いにする
    try {
      esRef.current = await startAlcoholCheck(
        (countdown) => {
          if (countdown > warmupMaxRef.current) warmupMaxRef.current = countdown;
          setAlcoholPhase("warming"); setWarmupCountdown(countdown);
        },
        () => { setAlcoholPhase("waiting"); grabAlcoholPhoto(); },
        (value) => { handleRawValue(value); },
        () => setAlcoholPhase("timeout"),
        () => setAlcoholPhase("timeout"),
        () => setAlcoholPhase("no_breath"),
      );
    } catch (sseErr) {
      console.warn('[startAlcohol] SSE接続失敗（チケット取得エラー）:', sseErr);
      setAlcoholPhase("timeout");
    }
  }, [doStamp, selectedVesselId]);

  const cancelAlcohol = useCallback(() => {
    manualAlcoInputRef.current = false;
    setManualAlcoInput(false);
    esRef.current?.close();
    esRef.current = null;
    usbCleanupRef.current?.();
    usbCleanupRef.current = null;
    stopCamera();
    setCameraStep('idle');
    // 本人確認写真(capturedPhotoRef)はここでは消さない。
    // アルコール検査後の打刻(doStamp)に添付する必要があるため。
    // 写真は各業務開始のstartCamera冒頭・ログアウト/戻る時に明示的にクリアされる。
    setAlcoholPhase("idle");
    setAlcoholValue(null);
    setWarmupCountdown(10);
    setBreathRetry(0);
    breathRetryRef.current = 0;
  }, [stopCamera]);

  /**
   * アルコール値の手入力を開く。
   * 開く前に検知器の読み取りを必ず止める。止めないと、あとから届く中断通知
   * （error:'cancelled'）で検査画面が閉じ、打刻画面まで戻ってしまう。
   * 画面は残したいので phase は idle にせず timeout のままにする。
   */
  const openManualAlcohol = useCallback(() => {
    esRef.current?.close();
    esRef.current = null;
    usbCleanupRef.current?.();
    usbCleanupRef.current = null;
    manualAlcoInputRef.current = true;
    setManualAlcoInput(true);
    setManualAlcoValue('');
    setAlcoholPhase("timeout");
  }, []);

  /**
   * 検知器が使えないときの逃げ道。
   * アルコールが必須の勤務では「測定せずに打刻」は出さない（必須の意味が無くなるため）。
   * 代わりに手入力へ誘導する。手入力は測定値が記録され、機器測定でないことが
   * 陸上管理者へアラートで通知されるので、記録が残らない打刻にはならない。
   */
  const renderAlcoholEscape = (label: string) => (
    alcoholRequiredNow ? (
      <button onClick={openManualAlcohol}
        style={{ width: "100%", height: 52, borderRadius: 14, border: "1.5px solid #bfdbfe", background: "#eff6ff", color: "#1d4ed8", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
        アルコール値を手動入力する
      </button>
    ) : (
      <button onClick={() => { cancelAlcohol(); doStamp(pendingActionRef.current, pendingWorkTypesRef.current.length > 0 ? pendingWorkTypesRef.current : undefined, undefined, capturedPhotoRef.current ?? undefined); }}
        style={{ background: "none", border: "none", color: "#94a3b8", fontSize: 13, cursor: "pointer", padding: "10px", display: "block", width: "100%", textAlign: "center" }}>
        {label}
      </button>
    )
  );

  // アルコール必須かどうかの判定（陸上が設定した再検査ルール）。
  // 判定はサーバーで行う。船内の共有端末は複数人が使うため、端末に前回値を持たせると
  // 直前に使った別人の記録を見てしまう。ログイン中の本人の記録から判定させる。
  const fetchAlcoholRequired = useCallback(async (): Promise<boolean> => {
    const vid = selectedVesselId || localStorage.getItem("catms_last_vessel_id") || "";
    try {
      const res = await apiFetch<{ data?: { required?: boolean; reasons?: string[] } }>(
        `/alcohol-rules/status${vid ? `?vessel_id=${encodeURIComponent(vid)}` : ""}`,
      );
      const required = !!res?.data?.required;
      // 圏外に備えて直近の判定を残す（次に電波が無いときはこれを使う）
      try { localStorage.setItem("catms_alcohol_required_cache", JSON.stringify({ required, at: Date.now() })); } catch { /* ignore */ }
      return required;
    } catch {
      // オフライン時: 直近の判定（6時間以内）を使い、それも無ければ従来どおり
      // 「前回業務終了からの経過」で判定する
      try {
        const c = JSON.parse(localStorage.getItem("catms_alcohol_required_cache") ?? "null") as { required: boolean; at: number } | null;
        if (c && Date.now() - c.at < 6 * 3600 * 1000) return c.required;
      } catch { /* ignore */ }
      const raw = localStorage.getItem("catms_last_work_end_at");
      if (!raw) return true;
      return Date.now() - new Date(raw).getTime() >= longBreakThresholdHours * 3600 * 1000;
    }
  }, [longBreakThresholdHours, selectedVesselId]);

  // 業務開始の候補。業務種別と特別作業は同じ内容を持ち合う運用なので、そのまま並べると
  // 「航海」「荷役」などが2回ずつ出ていた（多い船で64枚中24枚が重複）。業務を追加する画面と同じく名前で1本化し、
  // 記録のされ方を変えないよう業務種別を優先する。労働区分が「通常」以外のもの（食事・非常作業…）は
  // 特別作業として残す（出退勤として記録すると労働時間になってしまうため）。
  const startPickItems = useMemo<PickItem[]>(() => {
    const laborOn = hasFeature('labor_categories');
    const norm = (n: string) => (n ?? '').replace(/\s+/g, '').trim();
    const actByName = new Map(specialTypes.map(a => [norm(a.name), a] as const));
    const seen = new Set<string>();
    const out: PickItem[] = [];
    for (const w of vesselWorkTypes) {
      const k = norm(w.label);
      if (!k || seen.has(k)) continue;
      seen.add(k);
      const a = actByName.get(k);
      const b = laborOn ? bucketOf(a) : 'work';
      if (a && b !== 'work') out.push({ key: `s:${a.id}`, label: a.name, emoji: a.emoji || w.emoji, bucket: b });
      else out.push({ key: `w:${w.id}`, label: w.label, emoji: w.emoji, alco: (w.requires_alcohol ?? 0) === 1, bucket: 'work' });
    }
    for (const a of specialTypes) {
      const k = norm(a.name);
      if (!k || seen.has(k)) continue;
      seen.add(k);
      out.push({ key: `s:${a.id}`, label: a.name, emoji: a.emoji, bucket: laborOn ? bucketOf(a) : 'work' });
    }
    return out;
  }, [vesselWorkTypes, specialTypes]);

  /** 業務終了の選択を、業務開始で選んだ業務で埋めておく（多くの場合そのまま「次へ」で済む） */
  const prefillClockOutTypes = useCallback(() => {
    if (selectedWorkTypes.length > 0) return;
    const raw = (currentSession as { work_type?: string | null; work_type_start?: string | null } | null);
    const names = String(raw?.work_type_start || raw?.work_type || '').split(/[,、]/).map(x => x.trim()).filter(Boolean);
    const ids = names.map(n => vesselWorkTypes.find(w => w.label === n)?.id).filter((x): x is string => !!x);
    if (ids.length > 0) setSelectedWorkTypes(ids);
  }, [currentSession, selectedWorkTypes.length, vesselWorkTypes]);

  // 業務を選ぶ画面を開いたら、カードの見出しが画面の上に来るまで送る。
  // 横向きタブレットなどでは時計と船名で画面の上半分が埋まり、カードが下で切れて見えていた。
  useEffect(() => {
    if (workSelectStep !== "select" && clockOutStep !== "select") return;
    const t = setTimeout(() => {
      const el = punchAreaRef.current?.querySelector<HTMLElement>("[data-work-select]");
      el?.scrollIntoView({ block: "start", behavior: "smooth" });
    }, 60);
    return () => clearTimeout(t);
  }, [workSelectStep, clockOutStep]);

  // 業務選択画面を開く
  const openWorkSelect = useCallback((action: WorkSelectAction) => {
    if (isOperatorViewOnly) {
      showToast('運営アカウントでは打刻できません。船を選んで各画面の確認にお使いください。', 'error');
      return;
    }
    setWorkSelectAction(action);
    setWorkSelectTypes([]);
    setWorkSelectSpecial([]);
    setTimeBasedAlcoholRequired(false);
    // 選択画面を出しつつ、必要かどうかをサーバーに確認する（表示のちらつきを避けるため非同期）
    void fetchAlcoholRequired().then(setTimeBasedAlcoholRequired);
    setWorkSelectStep("select");
  }, [fetchAlcoholRequired]);

  // 業務選択完了後の処理
  const proceedFromWorkSelect = useCallback((selectedTypes: string[]) => {
    pendingActionRef.current = workSelectAction;
    pendingWorkTypesRef.current = selectedTypes;
    setWorkSelectStep("none");

    // アルコール必須判定（時刻ベース or 業務種別ベース）
    const typeRequired = selectedTypes.some(id => {
      const wt = vesselWorkTypes.find(w => w.id === id);
      return (wt?.requires_alcohol ?? 0) === 1;
    });
    const required = timeBasedAlcoholRequired || typeRequired;

    // 本人確認のため、業務開始は常にカメラ（顔写真撮影）を通す。
    // 撮影後、アルコール必須なら検査へ、そうでなければそのまま打刻する。
    pendingAlcoholRequiredRef.current = required;
    setAlcoholRequiredNow(required);
    startCamera();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workSelectAction, vesselWorkTypes, timeBasedAlcoholRequired, doStamp]);

  /** カメラ後（撮影 or スキップ）の遷移: アルコール必須なら検査、なければ打刻 */
  const afterCamera = useCallback((faceImage?: string) => {
    if (pendingAlcoholRequiredRef.current) { startAlcohol(); return; }
    doStamp(pendingActionRef.current, pendingWorkTypesRef.current.length > 0 ? pendingWorkTypesRef.current : undefined, undefined, faceImage);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doStamp]);

  // ── カメラ関連（アプリ内プレビュー） ──
  const startCamera = async () => {
    setCapturedPhoto(null);
    capturedPhotoRef.current = null;
    if (!navigator.mediaDevices?.getUserMedia) {
      // カメラ非対応環境（PC・非HTTPS等）でも打刻はブロックしない
      showToast('この環境ではカメラを利用できません。写真なしで進みます。', 'error');
      afterCamera(undefined);
      return;
    }
    let stream: MediaStream | null = null;
    try {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } });
      } catch {
        stream = await navigator.mediaDevices.getUserMedia({ video: true });
      }
    } catch {
      showToast('カメラにアクセスできません。写真なしで進みます。', 'error');
      afterCamera(undefined);
      return;
    }
    streamRef.current = stream;
    setCameraStep('preview');
    setTimeout(() => {
      if (videoRef.current && streamRef.current) {
        videoRef.current.srcObject = streamRef.current;
        videoRef.current.play().catch(() => {});
      }
    }, 150);
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 480;
    canvas.height = video.videoHeight || 640;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.75);
    setCapturedPhoto(dataUrl);
    capturedPhotoRef.current = dataUrl;
    stopCamera();
    setCameraStep('captured');
  };

  const retakePhoto = () => {
    setCapturedPhoto(null);
    capturedPhotoRef.current = null;
    setCameraStep('idle');
    setTimeout(startCamera, 100);
  };

  /**
   * 画面のカメラプレビューを使わず、裏でカメラを一瞬起動して1枚撮る。
   * アルコール測定中(息を吹いている最中)の本人確認写真に使う。失敗しても打刻は止めない。
   */
  const captureSnapshot = async (): Promise<string | undefined> => {
    try {
      if (!navigator.mediaDevices?.getUserMedia) return undefined;
      let stream: MediaStream;
      try { stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } }); }
      catch { stream = await navigator.mediaDevices.getUserMedia({ video: true }); }
      const video = document.createElement('video');
      video.srcObject = stream; video.muted = true;
      (video as HTMLVideoElement & { playsInline: boolean }).playsInline = true;
      await video.play().catch(() => {});
      await new Promise(r => setTimeout(r, 600)); // 露出安定待ち
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth || 480;
      canvas.height = video.videoHeight || 640;
      let url: string | undefined;
      const ctx = canvas.getContext('2d');
      if (ctx) { ctx.drawImage(video, 0, 0); url = canvas.toDataURL('image/jpeg', 0.7); }
      stream.getTracks().forEach(t => t.stop());
      return url;
    } catch { return undefined; }
  };
  captureSnapshotRef.current = captureSnapshot;

  const skipCamera = () => {
    stopCamera();
    setCameraStep('idle');
    setCapturedPhoto(null);
    capturedPhotoRef.current = null;
    afterCamera(undefined); // 写真なし → アルコール要否で分岐
  };

  const proceedFromCamera = () => {
    // 写真は capturedPhotoRef.current に保持済み
    stopCamera();
    setCameraStep('idle');
    afterCamera(capturedPhotoRef.current ?? undefined);
  };

  const handleNextPerson = () => {
    esRef.current?.close();
    esRef.current = null;
    usbCleanupRef.current?.();
    usbCleanupRef.current = null;
    setClockInDoneStep(0);
    setClockInDoneAlcohol(null);
    onLogout(); // React状態(user)をnullにしてLoginPageを表示
  };

  const handleContinue = () => {
    setClockInDoneStep(0);
    setClockInDoneAlcohol(null);
  };

  const timeParts = time.split(":");
  const hhmm = timeParts.slice(0, 2).join(":");
  const ss = timeParts[2] ?? "00";
  const sc = STATUS_CONFIG[sessionStatus];

  /** 管理者が船を選ぶカード（打刻画面に出す） */
  const adminVesselPicker = (

                  <div style={{ padding: "14px 18px", borderRadius: 14, background: "#eef2ff", border: "1.5px solid #c7d2fe" }}>
                    <div style={{ fontSize: 11, color: "#4338ca", fontWeight: 700, marginBottom: 6 }}>管理者として船を選ぶ</div>
                    <select value={selectedVesselId}
                      onChange={e => {
                        const id = e.target.value;
                        setSelectedVesselId(id);
                        const v = adminVessels.find(x => x.id === id);
                        if (v) {
                          localStorage.setItem("catms_admin_vessel_id", v.id);
                          localStorage.setItem("catms_last_vessel_id", v.id);
                          localStorage.setItem("catms_last_vessel_name", v.vessel_name);
                        }
                      }}
                      style={{ width: "100%", padding: "12px 14px", borderRadius: 10, border: "1.5px solid #c7d2fe", background: "#fff", fontSize: 15, fontWeight: 700, color: "#1a2b3c" }}>
                      <option value="">-- 船を選んでください --</option>
                      {adminVessels.map(v => <option key={v.id} value={v.id}>{v.vessel_name}</option>)}
                    </select>
                    <div style={{ fontSize: 11, color: "#6366f1", marginTop: 8, lineHeight: 1.7 }}>
                      {isOperatorViewOnly
                        ? '運営アカウントでは打刻できません。船を選ぶと、その船の画面を確認できます。'
                        : '選んだ船で業務開始できます。管理者の打刻として記録に印が付きます。'}
                    </div>
                  </div>
  );

  const alcoholActive = alcoholPhase !== "idle";
  // 乗船の登録が無い船員は業務開始できない（端末の船に未配乗／配乗が無く船も決まっていない）
  const needBoardFirst = sessionStatus === "idle" && !isAdminOperator
    && (!!boardingNeeded || (!selectedVesselId && hasFeature('crew_self_boarding')));
  const isNoBreath = alcoholPhase === "no_breath";
  // 詳細な通信ログは管理者（システム管理者・陸上管理者）操作時のみ表示。
  // 一般の船員には見せず、やさしいカウントダウンUIのみにする。
  const showDebug = ["system_admin", "shore_admin", "admin"].includes(user.user_role);

  // 出勤完了オーバーレイ（共有端末用）
  if (clockInDoneStep > 0) {
    const alco = clockInDoneAlcohol;
    // 閾値は会社ごとに変えられる。アラート設定の値をキャッシュから読む。
    const th = getAlcoholThresholds();
    const judgement = judgeAlcohol(alco, th);
    const isNormal  = judgement === "normal";
    const isWarning = judgement === "warning";
    const circleColor = isNormal  ? "linear-gradient(145deg, #10b981, #059669)"
                      : isWarning ? "linear-gradient(145deg, #f59e0b, #d97706)"
                      :             "linear-gradient(145deg, #ef4444, #dc2626)";
    const circleShadow = isNormal  ? "0 0 0 16px rgba(16,185,129,0.12), 0 20px 56px rgba(16,185,129,0.4)"
                       : isWarning ? "0 0 0 16px rgba(245,158,11,0.12), 0 20px 56px rgba(245,158,11,0.4)"
                       :             "0 0 0 16px rgba(239,68,68,0.12), 0 20px 56px rgba(239,68,68,0.4)";
    const dismissNotice = () => {
      setNoticePopup(null);
      apiFetch('/messages/mark-read', { method: 'POST', body: JSON.stringify({ partner_id: '__broadcast__' }) }).catch(() => {});
    };

    return (
      // justify-content は "safe center"＋スクロール可: 横画面タブレットでは高さが足りず、
      // 通常の center だと上下が切れて「次の方はこちら」に届かなくなるため
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "safe center", background: "#f8fafc", padding: "32px 24px", gap: 24, fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Hiragino Sans', sans-serif" }}>

        {/* お知らせポップアップ（未読ブロードキャストあり・step2以降に表示） */}
        {noticePopup && clockInDoneStep === 2 && doneKind === "in" && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "flex-end", zIndex: 200 }}>
            <div style={{ background: "white", borderRadius: "24px 24px 0 0", width: "100%", maxWidth: 480, margin: "0 auto", padding: "0 0 env(safe-area-inset-bottom)", maxHeight: "70dvh", display: "flex", flexDirection: "column" }}>
              {/* ヘッダー */}
              <div style={{ padding: "20px 20px 14px", borderBottom: "1px solid #f1f5f9", flexShrink: 0, display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 38, height: 38, borderRadius: "50%", background: "linear-gradient(135deg,#f59e0b,#d97706)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: "#0f172a" }}>お知らせ</div>
                  <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1 }}>未読 {noticePopup.length}件</div>
                </div>
              </div>
              {/* 一覧 */}
              <div style={{ flex: 1, overflowY: "auto", padding: "8px 0" }}>
                {noticePopup.map(n => (
                  <div key={n.id} style={{ padding: "14px 20px", borderBottom: "1px solid #f8fafc" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: "#0057d9" }}>{n.sender_name}</span>
                      <span style={{ fontSize: 11, color: "#94a3b8" }}>
                        {new Date(n.created_at).toLocaleString("ja-JP", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Tokyo" })}
                      </span>
                    </div>
                    <p style={{ margin: 0, fontSize: 14, color: "#1e293b", lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{n.content}</p>
                  </div>
                ))}
              </div>
              {/* 閉じるボタン */}
              <div style={{ padding: "16px 20px", flexShrink: 0 }}>
                <button onClick={dismissNotice} style={{ width: "100%", height: 56, borderRadius: 14, border: "none", background: "linear-gradient(145deg,#0057d9,#0369a1)", color: "white", fontSize: 16, fontWeight: 800, cursor: "pointer", boxShadow: "0 6px 20px rgba(0,87,217,0.35)" }}>
                  確認しました
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 判定サークル */}
        <div style={{ width: 160, height: 160, borderRadius: "50%", background: circleColor, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", boxShadow: circleShadow, gap: 6 }}>
          {isNormal
            ? <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            : isWarning
            ? <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            : <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
          }
          {clockInDoneStep === 1 && (
            <span style={{ fontSize: 11, color: "rgba(255,255,255,0.8)", fontWeight: 600 }}>処理中...</span>
          )}
        </div>

        {/* 名前・メッセージ */}
        <div style={{ textAlign: "center" }}>
          <p style={{ margin: "0 0 4px", fontSize: 14, color: "#94a3b8" }}>お疲れ様です</p>
          <p style={{ margin: "0 0 2px", fontSize: 24, fontWeight: 900, color: "#0f172a", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, flexWrap: "wrap" }}>
            {user.user_name}
            <FaceBadge userId={user.user_id} size="md" />
          </p>
          <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: isNormal ? "#059669" : isWarning ? "#d97706" : "#dc2626" }}>
            {doneKind === "out"
              ? (isNormal ? "業務を終了しました" : isWarning ? "業務を終了しました（上長確認要）" : "業務を終了しました（基準値超過）")
              : (isNormal ? "業務を開始しました" : isWarning ? "業務を開始しました（上長確認要）" : "業務開始不可")}
          </p>
        </div>

        {/* アルコール値 */}
        {alco != null && (
          <div style={{ background: isNormal ? "#ecfdf5" : isWarning ? "#fffbeb" : "#fef2f2", border: `1px solid ${isNormal ? "#a7f3d0" : isWarning ? "#fde68a" : "#fecaca"}`, borderRadius: 14, padding: "14px 28px", textAlign: "center", minWidth: 200 }}>
            <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>アルコール測定値</div>
            <div style={{ fontSize: 28, fontWeight: 900, color: isNormal ? "#059669" : isWarning ? "#d97706" : "#dc2626" }}>
              {alco.toFixed(3)} <span style={{ fontSize: 14, fontWeight: 600 }}>mg/L</span>
            </div>
            <div style={{ fontSize: 12, marginTop: 4, fontWeight: 700, color: isNormal ? "#059669" : isWarning ? "#d97706" : "#dc2626" }}>
              {isNormal  ? "正常（基準値未満）"
               : isWarning ? `要確認（${fmtMgL(th.warning_mgl)} 以上）`
               :             `法定基準値超過（${fmtMgL(th.critical_mgl)} 以上）`}
            </div>
          </div>
        )}

        {/* ボタン（step2のみ表示） */}
        {clockInDoneStep === 2 && (
          <div className="tablet-wide" style={{ width: "100%", maxWidth: 320, display: "flex", flexDirection: "column", gap: 12, marginTop: 8 }}>
            <button
              onClick={handleNextPerson}
              style={{ width: "100%", height: 72, borderRadius: 18, border: "none", background: "linear-gradient(145deg,#0057d9,#0369a1)", color: "white", fontSize: 18, fontWeight: 800, cursor: "pointer", boxShadow: "0 8px 24px rgba(0,87,217,0.4)", display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              次の方はこちら
            </button>
            <button
              onClick={handleContinue}
              style={{ width: "100%", height: 52, borderRadius: 14, border: "1.5px solid #e4e9f0", background: "white", color: "#5f6b7c", fontSize: 15, fontWeight: 600, cursor: "pointer" }}
            >
              このまま続ける
            </button>
          </div>
        )}

        {/* step1 のローディングドット */}
        {clockInDoneStep === 1 && (
          <div style={{ display: "flex", gap: 6 }}>
            {[0,1,2].map(i => (
              <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: "#cbd5e1", animation: `bounce 1s ${i * 0.2}s infinite` }} />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", background: "#f8fafc", fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Hiragino Sans', sans-serif" }}>

      {/* ヘッダー（スリム・1行にまとめて他の表示領域を広げる） */}
      <div className="app-header" style={{ background: "white", borderBottom: "1px solid #e4e9f0", padding: "0 16px 10px", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <AuthenticatedImage
            resource={user.avatar_path ? `avatars/${user.avatar_path}` : null}
            alt={user.user_name}
            imgStyle={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
            fallback={
              <div style={{ width: 36, height: 36, borderRadius: "50%", background: "linear-gradient(135deg, #0057d9, #38bdf8)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <span style={{ color: "white", fontSize: 15, fontWeight: 800 }}>{user.user_name.charAt(0)}</span>
              </div>
            }
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, color: "#1a2b3c", fontSize: 16, fontWeight: 800, display: "flex", alignItems: "center", gap: 6, overflow: "hidden" }}>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.user_name}</span>
              <FaceBadge userId={user.user_id} />
            </p>
            {currentSession?.vessel_name && (
              Number(currentSession.admin_operated ?? 0) === 1
                ? <p style={{ margin: "1px 0 0", color: "#6d28d9", fontSize: 11.5, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>🎭 {currentSession.vessel_name} 管理者の操作中（集計対象外）</p>
                : <p style={{ margin: "1px 0 0", color: "#0057d9", fontSize: 11.5, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>🚢 {currentSession.vessel_name} 乗務中</p>
            )}
          </div>
          <PushBell />
          <div title={isOnline ? "オンライン" : "オフライン"} style={{ width: 10, height: 10, borderRadius: "50%", background: isOnline ? "#16a34a" : "#dc2626", flexShrink: 0, boxShadow: `0 0 0 3px ${isOnline ? "rgba(22,163,74,0.15)" : "rgba(220,38,38,0.15)"}` }} />
        </div>
      </div>

      {/* オフラインバナー */}
      {!isOnline && (
        <div style={{ background: "#7c3a1e", color: "white", padding: "9px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
          <span>オフラインモード — データはローカルに保存されます</span>
          {pendingCount > 0 && <span style={{ background: "rgba(255,255,255,0.25)", borderRadius: 20, padding: "3px 12px", fontSize: 12, fontWeight: 800 }}>未送信 {pendingCount}件</span>}
        </div>
      )}
      {isOnline && pendingCount > 0 && (
        <div style={{ background: "#1d4ed8", color: "white", padding: "8px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 12, fontWeight: 600, flexShrink: 0 }}>
          <span>オンライン復帰 — バックグラウンドで同期中...</span>
          <span style={{ background: "rgba(255,255,255,0.2)", borderRadius: 20, padding: "2px 10px", fontSize: 11 }}>{pendingCount}件残</span>
        </div>
      )}

      {/* 役職未登録の警告: 自己乗船などで役職が入っていないと、船長などの役職に付いた機能が使えない（個人・共有端末とも） */}
      {missingPosition && (
        <div role="alert" style={{ margin: "8px 12px 0", padding: "10px 12px", borderRadius: 12, background: "#fef2f2", border: "1.5px solid #fca5a5", color: "#991b1b", fontSize: 12.5, lineHeight: 1.5, flexShrink: 0 }}>
          <div style={{ fontWeight: 900, fontSize: 13.5, marginBottom: 2 }}>⚠ 役職が登録されていません</div>
          現在の乗船に役職（船長・機関長など）が入っていないため、役職に付いた機能（証明書作成・書類の印刷・乗組員の勤怠閲覧など）が使えません。
          陸上の管理者に「配乗の役職登録」を依頼してください。登録されると自動で使えるようになります。
        </div>
      )}

      {/* トースト */}
      {toast && (
        <div style={{
          position: "fixed", top: 60, left: "50%", transform: "translateX(-50%)",
          background: toast.type === "error" ? "#dc2626" : "#0f172a",
          color: "white", padding: "12px 28px", borderRadius: 999,
          fontSize: 15, fontWeight: 700, whiteSpace: "nowrap",
          boxShadow: "0 8px 24px rgba(0,0,0,0.25)", zIndex: 100,
        }}>{toast.msg}</div>
      )}

      {/* メイン */}
      {/* justify-content は "safe center": 業務選択などでコンテンツがはみ出す場合、
          通常の center だと中央基準で上端が切れてスクロール到達不能になるため（航海当直が選べない不具合）。
          safe 非対応環境では flex-start 扱いになり、いずれにせよ全項目に到達できる。 */}
      <div ref={punchAreaRef} style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "safe center", padding: "16px 24px 8px" }}>

        {loading ? (
          <div style={{ textAlign: "center", color: "#94a3b8" }}>
            <div style={{ width: 36, height: 36, border: "3px solid #e4e9f0", borderTopColor: "#0057d9", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 12px" }} />
            <p style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>読み込み中...</p>
          </div>
        ) : isPersonalDevice() ? (
          /* ── 個人スマホ（閲覧モード）: 打刻UIは出さず、閲覧・連絡・報告への入り口を提供 ── */
          <div className="tablet-wide" style={{ width: "100%", maxWidth: 360, display: "flex", flexDirection: "column", gap: 14 }}>
            {/* ブラウザで開いているときだけ「ホーム画面に追加」の手順（追加済み・閉じた後は出ない） */}
            <InstallAppGuide />
            <div style={{ textAlign: "center", marginBottom: 4 }}>
              <p style={{ margin: "0 0 4px", fontSize: 16, color: "#1e293b", fontWeight: 700 }}>{date}</p>
              <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "center", gap: 2 }}>
                <span style={{ fontSize: 52, fontWeight: 900, color: "#0f172a", lineHeight: 1, letterSpacing: "-3px" }}>{hhmm}</span>
                <span style={{ fontSize: 20, fontWeight: 600, color: "#cbd5e1", marginBottom: 4 }}>:{ss}</span>
              </div>
            </div>
            <div style={{ background: "#eff6ff", border: "1.5px solid #bfdbfe", borderRadius: 16, padding: "14px 16px", display: "flex", gap: 10, alignItems: "flex-start" }}>
              <span style={{ fontSize: 22 }}>📱</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: "#1e40af", marginBottom: 3 }}>個人用アプリ（閲覧モード）</div>
                <div style={{ fontSize: 12, color: "#1e3a8a", lineHeight: 1.6 }}>
                  出退勤の打刻は<strong>船の管理端末</strong>から行ってください。<br />
                  このアプリでは勤怠の確認・修正・連絡・ヒヤリハット報告が利用できます。
                </div>
                {/* 管理者・代理ログイン中の人は、動作確認のため管理用アプリへ切替できる */}
                {isPrivilegedViewer() && (
                  <button onClick={() => switchAppVariant("managed")}
                    style={{ marginTop: 10, padding: "8px 14px", borderRadius: 10, border: "1px solid #4338ca", background: "#4338ca", color: "#fff", fontSize: 12.5, fontWeight: 800, cursor: "pointer" }}>
                    🖥 管理用アプリに切り替え（動作確認）
                  </button>
                )}
              </div>
            </div>
            {/* 乗船の自己登録（配乗漏れ防止）。フラグ有効な会社のみ */}
            {hasFeature('crew_self_boarding') && <SelfBoardingCard />}

            {/* ポータルの今（未読・最新お知らせ）。フラグ portal の会社のみ */}
            {hasFeature('portal') && <PortalSummaryCard />}

            {(() => {
              // 個人アプリは閲覧・申請・連絡が基本。書類・印刷／証明書作成は権限保持者にのみ表示
              // （2026-08-20 顧客要望: 個人スマホからも権限があれば印刷可能に）。
              // 縦一列だとボタンが多く操作しづらいので2列のコンパクトなタイルにまとめる。
              let pperms: Record<string, boolean> = {};
              try { pperms = JSON.parse(localStorage.getItem("catms_permissions") ?? "{}"); } catch { /* noop */ }
              // アラートは下部ナビから見られるのでタイルには置かない（画面を絞る）
              const items: [string, string, string][] = [
                ["📋", "勤怠の確認・修正", "/history"],
                ["🌴", "休暇申請",       "/leave"],
                ["💬", "連絡",           "/messages"],
                ["⚠️", "ヒヤリハット",   "/safety-report"],
              ];
              // 乗下船表は乗船・下船の登録と同じカードにまとめたので、
              // そのカードが出ないとき（自己登録OFF）だけタイルに出す
              if (hasFeature('crew_boarding_sheet') && !hasFeature('crew_self_boarding')) {
                items.push(["🗓", "乗下船表", "/boarding-sheet"]);
              }
              // 社内様式（船用品注文書・船員旅費精算書）は書類権限が無い船員も作れる
              if (pperms.admin_documents || hasFeature("extra_cert_forms")) items.push(["📝", "証明書作成", "/cert-create"]);
              if (pperms.print_documents) items.push(["📄", "書類・印刷", "/documents"]);
              // ポータルの会社: タイルを詰め込まず、入口は「メニュー」と「ポータル」の2つに絞る
              // （個別機能はスタート画面 /start とポータルホームから。2026-10-19 タイル過密の指摘対応）
              if (hasFeature('portal')) {
                items.unshift(["☰", "メニュー（何をする？）", "/start"]);
                items.push(["🏠", "ポータル", "/portal-home"]);
              }
              return (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, width: "100%" }}>
                  {items.map(([icon, title, path], i) => {
                    const spanFull = items.length % 2 === 1 && i === items.length - 1;
                    return (
                      <button key={path} onClick={() => nav(path)}
                        style={{
                          gridColumn: spanFull ? "1 / -1" : undefined,
                          padding: spanFull ? "16px" : "18px 10px", borderRadius: 16,
                          border: "1.5px solid #e4e9f0", background: "white", cursor: "pointer",
                          display: "flex", flexDirection: spanFull ? "row" : "column",
                          alignItems: "center", justifyContent: "center", gap: spanFull ? 10 : 8,
                          boxShadow: "0 1px 3px rgba(0,0,0,0.05)", minHeight: spanFull ? 0 : 76,
                        }}>
                        <span style={{ fontSize: 28, lineHeight: 1 }}>{icon}</span>
                        <span style={{ fontSize: 14, fontWeight: 800, color: "#1a2b3c", textAlign: "center" }}>{title}</span>
                      </button>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        ) : (
          <>
            {/* 日付・時刻・ステータス */}
            <div style={{ textAlign: "center", marginBottom: 28 }}>
              <p style={{ margin: "0 0 4px", fontSize: 18, color: "#1e293b", fontWeight: 700 }}>{date}</p>
              <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "center", gap: 2, marginBottom: 14 }}>
                <span style={{ fontSize: 68, fontWeight: 900, color: "#0f172a", lineHeight: 1, letterSpacing: "-4px" }}>{hhmm}</span>
                <span style={{ fontSize: 26, fontWeight: 600, color: "#cbd5e1", marginBottom: 6 }}>:{ss}</span>
              </div>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 7, background: sc.bg, border: `1px solid ${sc.border}`, borderRadius: 999, padding: "7px 20px" }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: sc.dot }} />
                <span style={{ color: sc.color, fontSize: 14, fontWeight: 700 }}>{sc.label}</span>
              </div>
            </div>

            {/* 船名表示（idle時のみ・選択不可） */}
            {sessionStatus === "idle" && clockOutStep === "none" && (
              <div className="tablet-wide" style={{ width: "100%", maxWidth: 320, marginBottom: 20 }}>
                {/* 管理者は船を選べるようにする（勤務中は稼働中セッションの船で固定） */}
                {isAdminOperator && adminVessels.length > 0 && !currentSession ? adminVesselPicker
                  : vessels.length > 0 && selectedVesselId ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 18px", borderRadius: 14, background: "#f0f7ff", border: "1.5px solid #bfdbfe" }}>
                    <AuthenticatedImage
                      resource={vessels[0].image_path ? `vessel-images/${vessels[0].image_path}` : null}
                      alt={vessels[0].vessel_name}
                      imgStyle={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
                      fallback={
                        <div style={{ width: 36, height: 36, borderRadius: "50%", background: "linear-gradient(135deg, #0057d9, #38bdf8)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 20a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8l-7-7H4a2 2 0 0 0-2 2z"/><polyline points="14 2 14 8 20 8"/></svg>
                        </div>
                      }
                    />
                    <div>
                      <div style={{ fontSize: 11, color: "#5f6b7c", fontWeight: 600, marginBottom: 1 }}>船名</div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: "#1a2b3c" }}>{vessels[0].vessel_name}</div>
                    </div>
                  </div>
                ) : isAdminOperator && adminVessels.length > 0 ? adminVesselPicker
                  // 配乗の無い船員は、自分で乗船を登録できる会社ならその場で登録してもらう
                  : !isAdminOperator && hasFeature('crew_self_boarding') ? <SelfBoardingCard onChanged={loadInitialData} />
                  // ポータルの会社: 配乗の無い人（パート・陸上勤務）に警告を出さず、ポータルへ静かに案内する
                  // （「船舶が割り当てられていません」は陸上勤務には無関係で、画面が警告だらけに見えていた）
                  : hasFeature('portal') ? (
                  <div style={{ padding: "18px 18px", borderRadius: 14, background: "#f0faf8", border: "1.5px solid #bfe3dc", textAlign: "center" }}>
                    <div style={{ fontSize: 14.5, fontWeight: 800, color: "#0c5f59", marginBottom: 4 }}>船の配乗がないため、ここでの打刻はありません</div>
                    <div style={{ fontSize: 12.5, color: "#3d4248", marginBottom: 12 }}>会社の連絡・申請・マニュアルはポータルからどうぞ</div>
                    <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
                      <button onClick={() => nav('/start')}
                        style={{ padding: "11px 22px", borderRadius: 10, border: "none", background: "#0f766e", color: "#fff", fontSize: 13.5, fontWeight: 800, cursor: "pointer" }}>
                        メニューを開く
                      </button>
                      <button onClick={() => nav('/portal-home')}
                        style={{ padding: "11px 22px", borderRadius: 10, border: "1.5px solid #0f766e", background: "#fff", color: "#0c5f59", fontSize: 13.5, fontWeight: 800, cursor: "pointer" }}>
                        ポータルホーム
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ padding: "16px 18px", borderRadius: 14, background: "#fff7ed", border: "1.5px solid #fed7aa", textAlign: "center" }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#9a3412", marginBottom: 4 }}>船舶が割り当てられていません</div>
                    <div style={{ fontSize: 12, color: "#c2410c", marginBottom: 10 }}>管理者に乗組員配置の設定を依頼してください</div>
                    <button onClick={loadInitialData}
                      style={{ padding: "8px 20px", borderRadius: 8, border: "1px solid #fed7aa", background: "white", color: "#9a3412", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                      再読み込み
                    </button>
                  </div>
                )}
                {boardingNeeded && (
                  <BoardingRequiredCard vesselId={boardingNeeded.id} vesselName={boardingNeeded.name} onBoarded={loadInitialData} />
                )}
                {/* 配乗の無い船で管理者が打刻する（船員デモを含む）→ 勤怠の集計・休息の判定に入らないことを明示 */}
                {isAdminOperator && !isOperatorViewOnly && selectedVesselId && (isImpersonated || ownOnBoardVesselId !== selectedVesselId) && (
                  <div style={{ marginTop: 10, padding: "10px 14px", borderRadius: 12, background: "#f5f3ff", border: "1px solid #ddd6fe", fontSize: 12, color: "#5b21b6", lineHeight: 1.7, textAlign: "left" }}>
                    🎭 <strong>管理者の操作</strong>として記録します。動作確認・説明用の扱いで、勤怠の集計と休息時間の判定には含まれません。
                  </div>
                )}
              </div>
            )}

            {/* 作業開始時の業務選択（出勤・休憩終了共通） */}
            {workSelectStep === "select" && (() => {
              const typeRequired = workSelectTypes.some(id => {
                const wt = vesselWorkTypes.find(w => w.id === id);
                return (wt?.requires_alcohol ?? 0) === 1;
              });
              const isRequired = timeBasedAlcoholRequired || typeRequired;
              const actionLabel = "業務開始";
              return (
                <div data-work-select className="tablet-wide" style={{ width: "100%", maxWidth: 340, background: "white", borderRadius: 20, border: "1px solid #e4e9f0", padding: "24px 20px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", scrollMarginTop: 8 }}>
                  {/* セクションヘッダー */}
                  <div style={{ width: 52, height: 52, borderRadius: "50%", background: "linear-gradient(135deg,#0057d9,#38bdf8)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/></svg>
                  </div>
                  <p style={{ fontSize: 20, fontWeight: 800, color: "#0f172a", margin: "0 0 6px", textAlign: "center" }}>業務を選択</p>
                  <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 20px", textAlign: "center" }}>複数選択できます</p>

                  {/* アルコール必須バッジ */}
                  {isRequired && (
                    <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "6px 12px", marginBottom: 10, textAlign: "center", fontSize: 12, fontWeight: 700, color: "#92400e", display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#92400e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                        アルコール検査が必要です
                      </span>
                      {timeBasedAlcoholRequired && (
                        <span style={{ display: "block", fontSize: 11, fontWeight: 500, marginTop: 2, color: "#b45309" }}>
                          （長時間の離席後）
                        </span>
                      )}
                      {typeRequired && (
                        <span style={{ display: "block", fontSize: 11, fontWeight: 500, marginTop: 2, color: "#b45309" }}>
                          （選択中の業務に必須設定あり）
                        </span>
                      )}
                    </div>
                  )}

                  {/* 選択済みタグ（統合表示では臨時業務も同じ列に並べる） */}
                  {(workSelectTypes.length > 0 || (hasFeature('unified_work_types') && workSelectSpecial.length > 0)) && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10, justifyContent: "center" }}>
                      {hasFeature('unified_work_types') && workSelectSpecial.map(id => {
                        const s2 = specialTypes.find(x => x.id === id);
                        if (!s2) return null;
                        return (
                          <div key={id} style={{ display: "flex", alignItems: "center", gap: 5, background: "#0057d9", color: "white", padding: "5px 12px", borderRadius: 999, fontSize: 13, fontWeight: 700 }}>
                            <span>{s2.emoji} {s2.name}</span>
                            <span onClick={() => setWorkSelectSpecial(p => p.filter(x => x !== id))} style={{ cursor: "pointer", marginLeft: 2, opacity: 0.75, fontSize: 15, lineHeight: 1 }}>×</span>
                          </div>
                        );
                      })}
                      {workSelectTypes.map(id => {
                        const w = vesselWorkTypes.find(x => x.id === id);
                        if (!w) return null;
                        return (
                          <div key={id} style={{ display: "flex", alignItems: "center", gap: 5, background: "#0057d9", color: "white", padding: "5px 12px", borderRadius: 999, fontSize: 13, fontWeight: 700 }}>
                            <span>{w.label}</span>
                            {(w.requires_alcohol ?? 0) === 1 && <span style={{ fontSize: 10, opacity: 0.85, background: "rgba(255,255,255,0.25)", borderRadius: 4, padding: "1px 4px" }}>酒</span>}
                            <span onClick={() => setWorkSelectTypes(p => p.filter(x => x !== id))} style={{ cursor: "pointer", marginLeft: 2, opacity: 0.75, fontSize: 15, lineHeight: 1 }}>×</span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* 統合表示: 通常業務と臨時業務を1つのリストにまとめる（区分を出さない）。
                      選択状態は従来どおり workSelectTypes / workSelectSpecial に分けて持つため、
                      アルコール必須判定や打刻後の記録の作り分けはそのまま動く。 */}
                  {hasFeature('unified_work_types') ? (
                    <WorkTypePicker items={startPickItems} usageScope={selectedVesselId}
                      selected={[...workSelectTypes.map(id => `w:${id}`), ...workSelectSpecial.map(id => `s:${id}`)]}
                      onToggle={key => {
                        const id = key.slice(2);
                        if (key.startsWith('s:')) setWorkSelectSpecial(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
                        else setWorkSelectTypes(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
                      }} />
                  ) : (<>
                    <WorkTypePicker usageScope={selectedVesselId}
                      items={vesselWorkTypes.map(w => ({ key: w.id, label: w.label, emoji: w.emoji, alco: (w.requires_alcohol ?? 0) === 1 }))}
                      selected={workSelectTypes}
                      onToggle={id => setWorkSelectTypes(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id])} />
                    {/* 特別作業（設定されている船のみ表示）。通常業務が無くてもこれだけで業務開始できる。 */}
                    {specialTypes.length > 0 && (
                      <div style={{ marginTop: 10 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, margin: "2px 0 8px" }}>
                          <span style={{ fontSize: 13, fontWeight: 800, color: "#7c3aed" }}>特別作業</span>
                          <span style={{ fontSize: 11, color: "#94a3b8" }}>通常業務が無くても選べます</span>
                        </div>
                        <WorkTypePicker usageScope={selectedVesselId ? `${selectedVesselId}:special` : null} accent="#7c3aed" initialCount={4}
                          items={specialTypes.map(t => ({ key: t.id, label: t.name, emoji: t.emoji }))}
                          selected={workSelectSpecial}
                          onToggle={id => setWorkSelectSpecial(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id])} />
                      </div>
                    )}
                  </>)}

                  {/* 進むボタン。通常業務・特別作業のどちらも選ばなくても進める（打刻だけしたいケース対応） */}
                  <button
                    onClick={() => {
                      recordWorkUsage(selectedVesselId, hasFeature('unified_work_types')
                        ? [...workSelectTypes.map(id => `w:${id}`), ...workSelectSpecial.map(id => `s:${id}`)]
                        : workSelectTypes);
                      if (!hasFeature('unified_work_types')) recordWorkUsage(selectedVesselId ? `${selectedVesselId}:special` : null, workSelectSpecial);
                      proceedFromWorkSelect([...workSelectTypes, ...workSelectSpecial]);
                    }}
                    style={{ width: "100%", height: 72, borderRadius: 18, border: "none", background: isRequired ? "linear-gradient(145deg,#d97706,#b45309)" : "linear-gradient(145deg,#059669,#047857)", color: "white", fontSize: 18, fontWeight: 800, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginTop: 16, boxShadow: isRequired ? "0 8px 24px rgba(217,119,6,0.4)" : "0 8px 24px rgba(5,150,105,0.4)" }}
                  >
                    {workSelectTypes.length === 0 && workSelectSpecial.length === 0
                      ? (hasFeature('unified_work_types') ? `業務を選ばずに${actionLabel}` : `通常業務なしで${actionLabel}`)
                      : isRequired ? `${actionLabel}（アルコール検査あり）`
                      : `${actionLabel}する`}
                  </button>

                  <button
                    onClick={() => { setWorkSelectStep("none"); setWorkSelectTypes([]); setWorkSelectSpecial([]); }}
                    style={{ width: "100%", height: 52, borderRadius: 14, border: "1.5px solid #e4e9f0", background: "white", color: "#5f6b7c", fontSize: 15, fontWeight: 600, cursor: "pointer", marginTop: 10 }}
                  >
                    キャンセル
                  </button>
                </div>
              );
            })()}

            {/* 退勤フロー：業務選択 */}
            {clockOutStep === "select" && (
              <div data-work-select className="tablet-wide" style={{ width: "100%", maxWidth: 340, background: "white", borderRadius: 20, border: "1px solid #e4e9f0", padding: "24px 20px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", scrollMarginTop: 8 }}>
                {/* セクションヘッダー */}
                <div style={{ width: 52, height: 52, borderRadius: "50%", background: "linear-gradient(135deg,#0057d9,#38bdf8)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/></svg>
                </div>
                <p style={{ fontSize: 20, fontWeight: 800, color: "#0f172a", margin: "0 0 6px", textAlign: "center" }}>業務を選択</p>
                <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 20px", textAlign: "center" }}>複数選択できます</p>
                {selectedWorkTypes.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14, justifyContent: "center" }}>
                    {selectedWorkTypes.map(id => {
                      const w = vesselWorkTypes.find(x => x.id === id)!;
                      return (
                        <div key={id} style={{ display: "flex", alignItems: "center", gap: 5, background: "#0057d9", color: "white", padding: "5px 12px", borderRadius: 999, fontSize: 13, fontWeight: 700 }}>
                          <span>{w.label}</span>
                          <span onClick={() => setSelectedWorkTypes(p => p.filter(x => x !== id))} style={{ cursor: "pointer", marginLeft: 2, opacity: 0.75, fontSize: 15, lineHeight: 1 }}>×</span>
                        </div>
                      );
                    })}
                  </div>
                )}
                <WorkTypePicker usageScope={selectedVesselId}
                  items={vesselWorkTypes.map(w => ({ key: w.id, label: w.label, emoji: w.emoji }))}
                  selected={selectedWorkTypes}
                  onToggle={id => setSelectedWorkTypes(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id])} />
                <button onClick={() => { if (selectedWorkTypes.length === 0) return; recordWorkUsage(selectedVesselId, selectedWorkTypes); setClockOutStep("confirm"); }}
                  disabled={selectedWorkTypes.length === 0}
                  style={{ width: "100%", height: 72, borderRadius: 18, border: "none", background: selectedWorkTypes.length > 0 ? "linear-gradient(145deg,#ef4444,#dc2626)" : "#e2e8f0", color: selectedWorkTypes.length > 0 ? "white" : "#94a3b8", fontSize: 18, fontWeight: 800, cursor: selectedWorkTypes.length > 0 ? "pointer" : "not-allowed", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginTop: 16, boxShadow: selectedWorkTypes.length > 0 ? "0 8px 24px rgba(239,68,68,0.4)" : "none" }}>
                  次へ → ({selectedWorkTypes.length}件選択中)
                </button>
                <button onClick={() => { setClockOutStep("none"); setSelectedWorkTypes([]); }}
                  style={{ width: "100%", height: 52, borderRadius: 14, border: "1.5px solid #e4e9f0", background: "white", color: "#5f6b7c", fontSize: 15, fontWeight: 600, cursor: "pointer", marginTop: 10 }}>
                  キャンセル
                </button>
              </div>
            )}

            {/* 退勤フロー：確認 */}
            {clockOutStep === "confirm" && (
              <div className="tablet-wide" style={{ width: "100%", maxWidth: 320, textAlign: "center", background: "white", borderRadius: 20, border: "1px solid #e4e9f0", padding: "24px 20px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
                {/* セクションヘッダー */}
                <div style={{ width: 52, height: 52, borderRadius: "50%", background: "linear-gradient(135deg,#0057d9,#38bdf8)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                </div>
                <p style={{ fontSize: 20, fontWeight: 800, color: "#0f172a", margin: "0 0 6px", textAlign: "center" }}>業務終了確認</p>
                <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 20px", textAlign: "center" }}>{date}　{hhmm}</p>
                <div style={{ height: 1, background: "#f1f5f9", margin: "0 0 14px" }} />
                <p style={{ fontSize: 12, color: "#64748b", fontWeight: 600, margin: "0 0 10px", textAlign: "left" }}>今回の業務内容</p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center", marginBottom: 4 }}>
                  {selectedWorkTypes.map(id => {
                    const w = vesselWorkTypes.find(x => x.id === id)!;
                    return (
                      <div key={id} style={{ display: "flex", alignItems: "center", gap: 6, background: "#eff6ff", border: "1px solid #bfdbfe", padding: "6px 16px", borderRadius: 999 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: "#0057d9" }}>{w.label}</span>
                      </div>
                    );
                  })}
                </div>
                <button onClick={() => doStamp("clock_out", selectedWorkTypes)} disabled={stamping}
                  style={{ width: "100%", height: 72, borderRadius: 18, border: "none", background: stamping ? "#e2e8f0" : "linear-gradient(145deg,#ef4444,#dc2626)", color: stamping ? "#94a3b8" : "white", fontSize: 18, fontWeight: 800, cursor: "pointer", boxShadow: stamping ? "none" : "0 8px 24px rgba(239,68,68,0.4)", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginTop: 16 }}>
                  {stamping ? "処理中..." : "業務終了確定"}
                </button>
                <button onClick={() => setClockOutStep("select")}
                  style={{ width: "100%", height: 52, borderRadius: 14, border: "1.5px solid #e4e9f0", background: "white", color: "#5f6b7c", fontSize: 15, fontWeight: 600, cursor: "pointer", marginTop: 10 }}>
                  ← 業務内容を変更
                </button>
              </div>
            )}

            {/* アルコールチェック画面（出勤打刻後・インプレース） */}
            {(alcoholActive || manualAlcoInput) && (
              <div className="tablet-wide" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 20, width: "100%", maxWidth: 320, background: "white", borderRadius: 20, border: "1px solid #e4e9f0", padding: "24px 20px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
                {alcoholPhase === "warming" && (
                  <>
                    <div style={{ position: "relative", width: 150, height: 150, flexShrink: 0 }}>
                      <svg width="150" height="150" style={{ position: "absolute", top: 0, left: 0, transform: "rotate(-90deg)" }}>
                        <circle cx="75" cy="75" r="66" fill="none" stroke="rgba(99,102,241,0.15)" strokeWidth="9" />
                        <circle cx="75" cy="75" r="66" fill="none" stroke="#6366f1" strokeWidth="9"
                          strokeDasharray={`${2 * Math.PI * 66}`}
                          strokeDashoffset={`${2 * Math.PI * 66 * (warmupCountdown / Math.max(warmupMaxRef.current, 1))}`}
                          strokeLinecap="round" style={{ transition: "stroke-dashoffset 0.8s ease" }} />
                      </svg>
                      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "linear-gradient(145deg, #6366f1, #4f46e5)", borderRadius: "50%", margin: 9 }}>
                        <span style={{ fontSize: 36, fontWeight: 900, color: "white", lineHeight: 1 }}>{warmupCountdown}</span>
                        <span style={{ fontSize: 11, color: "rgba(255,255,255,0.7)", fontWeight: 600, marginTop: 2 }}>秒</span>
                      </div>
                    </div>
                    <p style={{ fontSize: 20, fontWeight: 800, color: "#0f172a", margin: 0, textAlign: "center" }}>測定の準備中です</p>
                    <p style={{ fontSize: 14, color: "#64748b", margin: 0, textAlign: "center" }}>あと <strong style={{ color: "#4f46e5", fontSize: 16 }}>{warmupCountdown}</strong> 秒で測定できます<br />そのままお待ちください</p>
                    {showDebug && usbDebugLog.length > 0 && (
                      <div style={{ background: "#1e293b", borderRadius: 8, padding: "8px 12px", width: "100%", maxHeight: 160, overflowY: "auto" }}>
                        {usbDebugLog.map((l, i) => <p key={i} style={{ margin: "1px 0", fontSize: 10, color: "#94a3b8", fontFamily: "monospace", wordBreak: "break-all" }}>{l}</p>)}
                      </div>
                    )}
                    <button onClick={cancelAlcohol} style={{ width: "100%", height: 52, borderRadius: 14, border: "1.5px solid #e4e9f0", background: "white", color: "#5f6b7c", fontSize: 15, fontWeight: 600, cursor: "pointer", marginTop: 0 }}>キャンセル</button>
                    {renderAlcoholEscape("デバイスなしで打刻する")}
                  </>
                )}
                {alcoholPhase === "waiting" && (
                  <>
                    <div style={{ position: "relative", width: 150, height: 150, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <div style={{ position: "absolute", width: 150, height: 150, borderRadius: "50%", background: "rgba(59,130,246,0.08)", animation: "pulse 1.8s ease-in-out infinite" }} />
                      <div style={{ position: "absolute", width: 125, height: 125, borderRadius: "50%", background: "rgba(59,130,246,0.12)", animation: "pulse 1.8s ease-in-out 0.3s infinite" }} />
                      <div style={{ width: 110, height: 110, borderRadius: "50%", background: "linear-gradient(145deg, #3b82f6, #2563eb)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: breathLeft !== null ? 0 : 6, boxShadow: "0 8px 32px rgba(59,130,246,0.4)" }}>
                        {breathLeft !== null ? (
                          <>
                            <span style={{ fontSize: 46, fontWeight: 900, color: "white", lineHeight: 1 }}>{breathLeft}</span>
                            <span style={{ fontSize: 11, color: "rgba(255,255,255,0.8)", fontWeight: 700 }}>秒</span>
                          </>
                        ) : (
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.59 4.59A2 2 0 1 1 11 8H2m10.59 11.41A2 2 0 1 0 14 16H2m15.73-8.27A2.5 2.5 0 1 1 19.5 12H2"/></svg>
                        )}
                      </div>
                    </div>
                    {breathLeft !== null ? (
                      breathLeft > 0 ? (
                        <>
                          <p style={{ fontSize: 20, fontWeight: 800, color: "#0f172a", margin: 0, textAlign: "center" }}>息を吹き続けてください</p>
                          <p style={{ fontSize: 13, color: "#64748b", margin: 0, textAlign: "center", lineHeight: 1.6 }}>
                            マウスピースに向かって強く、<br />あと <strong style={{ color: "#2563eb", fontSize: 16 }}>{breathLeft}</strong> 秒 止めずに吹き続けてください
                          </p>
                        </>
                      ) : (
                        <>
                          <p style={{ fontSize: 20, fontWeight: 800, color: "#0f172a", margin: 0, textAlign: "center" }}>そのまま！まもなく結果が出ます</p>
                          <p style={{ fontSize: 13, color: "#64748b", margin: 0, textAlign: "center", lineHeight: 1.6 }}>
                            結果が表示されるまで<br />吹き続けてください
                          </p>
                        </>
                      )
                    ) : (
                      <>
                        <p style={{ fontSize: 20, fontWeight: 800, color: "#0f172a", margin: 0, textAlign: "center" }}>息を吹きかけてください</p>
                        <p style={{ fontSize: 13, color: "#64748b", margin: 0, textAlign: "center", lineHeight: 1.6 }}>
                          検知器のマウスピースに向かって<br />ゆっくり深く <strong>2〜3秒</strong> 息を吹き込んでください
                        </p>
                      </>
                    )}
                    <div style={{ background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 10, padding: "8px 16px", fontSize: 12, color: "#0369a1", textAlign: "center", fontWeight: 600 }}>
                      短すぎると再測定になります
                    </div>
                    <button onClick={cancelAlcohol} style={{ width: "100%", height: 52, borderRadius: 14, border: "1.5px solid #e4e9f0", background: "white", color: "#5f6b7c", fontSize: 15, fontWeight: 600, cursor: "pointer", marginTop: 0 }}>キャンセル</button>
                    {renderAlcoholEscape("デバイスなしで打刻する")}
                  </>
                )}
                {/* 呼気未検知 → 再検査を促す（エラーではない） */}
                {isNoBreath && (
                  <>
                    <div style={{ width: 140, height: 140, borderRadius: "50%", background: "linear-gradient(145deg,#0057d9,#0369a1)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8, boxShadow: "0 0 0 14px rgba(0,87,217,0.12), 0 16px 48px rgba(0,87,217,0.35)", flexShrink: 0 }}>
                      <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.59 4.59A2 2 0 1 1 11 8H2m10.59 11.41A2 2 0 1 0 14 16H2m15.73-8.27A2.5 2.5 0 1 1 19.5 12H2"/></svg>
                      {breathRetry > 0 && (
                        <span style={{ fontSize: 11, color: "rgba(255,255,255,0.85)", fontWeight: 700 }}>{breathRetry}回目</span>
                      )}
                    </div>
                    <p style={{ fontSize: 20, fontWeight: 800, color: "#0f172a", margin: 0, textAlign: "center" }}>息が感知されませんでした</p>
                    <div style={{ background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 14, padding: "14px 18px", width: "100%", textAlign: "center" }}>
                      <p style={{ fontSize: 13, color: "#0369a1", margin: 0, fontWeight: 600, lineHeight: 1.6 }}>
                        マウスピースに口をしっかり当て、<br />
                        ゆっくりと深く息を吹き込んでください。
                      </p>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 10, width: "100%" }}>
                      <button onClick={startAlcohol} style={{ width: "100%", height: 72, borderRadius: 18, border: "none", background: "linear-gradient(145deg,#0057d9,#0369a1)", color: "white", fontSize: 18, fontWeight: 800, cursor: "pointer", boxShadow: "0 8px 24px rgba(0,87,217,0.4)", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginTop: 0 }}>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.59 4.59A2 2 0 1 1 11 8H2m10.59 11.41A2 2 0 1 0 14 16H2m15.73-8.27A2.5 2.5 0 1 1 19.5 12H2"/></svg>
                        もう一度測定する
                      </button>
                      <button onClick={cancelAlcohol} style={{ width: "100%", height: 52, borderRadius: 14, border: "1.5px solid #e4e9f0", background: "white", color: "#5f6b7c", fontSize: 15, fontWeight: 600, cursor: "pointer", marginTop: 0 }}>
                        キャンセル
                      </button>
                      {renderAlcoholEscape("デバイスなしで打刻する")}
                    </div>
                  </>
                )}

                {/* タイムアウト（デバイスが全く応答しなかった場合） */}
                {alcoholPhase === "timeout" && !manualAlcoInput && (
                  <>
                    <div style={{ width: 140, height: 140, borderRadius: "50%", background: "linear-gradient(145deg, #94a3b8, #64748b)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 0 0 14px rgba(100,116,139,0.1), 0 16px 48px rgba(100,116,139,0.3)", flexShrink: 0 }}>
                      <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="1" y1="1" x2="23" y2="23"/><path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55"/><path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39"/><path d="M10.71 5.05A16 16 0 0 1 22.56 9"/><path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88"/><path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><line x1="12" y1="20" x2="12.01" y2="20"/></svg>
                    </div>
                    <p style={{ fontSize: 20, fontWeight: 800, color: "#0f172a", margin: 0, textAlign: "center" }}>デバイスが応答しませんでした</p>
                    {/* 開発者専用アプリが原因と次にやることを返したときはそれを出す（「抜き挿しして」など） */}
                    <p style={{ fontSize: 13, color: "#64748b", margin: 0, textAlign: "center", lineHeight: 1.6 }}>
                      {usbDebugLog.filter(l => l.startsWith("ERR: ")).pop()?.slice(5) || "AN-S126aが接続されているか確認してください"}
                    </p>
                    {showDebug && usbDebugLog.length > 0 && (
                      <div style={{ background: "#1e293b", borderRadius: 8, padding: "8px 12px", width: "100%", maxHeight: 120, overflowY: "auto" }}>
                        {usbDebugLog.map((l, i) => <p key={i} style={{ margin: "1px 0", fontSize: 10, color: "#94a3b8", fontFamily: "monospace", wordBreak: "break-all" }}>{l}</p>)}
                      </div>
                    )}
                    <div style={{ display: "flex", flexDirection: "column", gap: 10, width: "100%" }}>
                      <button onClick={startAlcohol} style={{ width: "100%", height: 72, borderRadius: 18, border: "none", background: "linear-gradient(145deg,#0057d9,#0369a1)", color: "white", fontSize: 18, fontWeight: 800, cursor: "pointer", boxShadow: "0 8px 24px rgba(0,87,217,0.4)", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginTop: 0 }}>再試行</button>
                      <button onClick={cancelAlcohol} style={{ width: "100%", height: 52, borderRadius: 14, border: "1.5px solid #e4e9f0", background: "white", color: "#5f6b7c", fontSize: 15, fontWeight: 600, cursor: "pointer", marginTop: 0 }}>キャンセル</button>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%" }}>
                      <button onClick={openManualAlcohol}
                        style={{ width: "100%", height: 52, borderRadius: 14, border: "1.5px solid #bfdbfe", background: "#eff6ff", color: "#1d4ed8", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
                        アルコール値を手動入力する
                      </button>
                      {!alcoholRequiredNow && (
                        <button onClick={() => { cancelAlcohol(); doStamp(pendingActionRef.current, pendingWorkTypesRef.current.length > 0 ? pendingWorkTypesRef.current : undefined, undefined, capturedPhotoRef.current ?? undefined); }} style={{ background: "none", border: "none", color: "#94a3b8", fontSize: 13, cursor: "pointer", padding: "10px", display: "block", width: "100%", textAlign: "center" }}>
                          デバイスなし・写真のみで打刻する
                        </button>
                      )}
                    </div>
                  </>
                )}

                {/* 手動アルコール入力フォーム */}
                {manualAlcoInput && (
                  <>
                    <div style={{ width: 52, height: 52, borderRadius: "50%", background: "linear-gradient(135deg,#0057d9,#38bdf8)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 0", flexShrink: 0 }}>
                      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                    </div>
                    <p style={{ fontSize: 20, fontWeight: 800, color: "#0f172a", margin: 0, textAlign: "center" }}>アルコール値を入力</p>
                    <p style={{ fontSize: 13, color: "#64748b", margin: 0, textAlign: "center" }}>
                      呼気式検知器の数値を mg/L 単位で入力してください
                    </p>
                    <div style={{ width: "100%", maxWidth: 240 }}>
                      <div style={{ display: "flex", alignItems: "center", border: "2px solid #6366f1", borderRadius: 12, overflow: "hidden", background: "white" }}>
                        <input
                          type="number" step="0.001" min="0" max="1"
                          value={manualAlcoValue}
                          onChange={e => setManualAlcoValue(e.target.value)}
                          placeholder="例: 0.000"
                          style={{ flex: 1, border: "none", padding: "14px 16px", fontSize: 22, fontWeight: 700, outline: "none", textAlign: "center", color: "#0f172a" }}
                        />
                        <span style={{ padding: "0 14px", color: "#64748b", fontWeight: 600, fontSize: 13 }}>mg/L</span>
                      </div>
                      {manualAlcoValue !== '' && (() => {
                        const v = Number(manualAlcoValue);
                        const j = judgeAlcohol(v);
                        const color = j === 'critical' ? '#dc2626' : j === 'warning' ? '#d97706' : '#059669';
                        const label = j === 'critical' ? '出勤不可' : j === 'warning' ? '要上長確認' : '正常';
                        return <div style={{ textAlign: "center", marginTop: 8, fontSize: 14, fontWeight: 700, color }}>{label}</div>;
                      })()}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 10, width: "100%" }}>
                      <button
                        onClick={() => {
                          const v = Number(manualAlcoValue);
                          if (isNaN(v) || v < 0) return;
                          // 危険閾値の超過も含めて常にサーバーへ送信。0(しらふ)も測定値として送る。
                          // 顔写真(capturedPhotoRef)も必ず添付し、手入力フラグを立てる。
                          cancelAlcohol();
                          manualAlcoInputRef.current = false;
                          setManualAlcoInput(false);
                          doStamp(pendingActionRef.current, pendingWorkTypesRef.current.length > 0 ? pendingWorkTypesRef.current : undefined, v, capturedPhotoRef.current ?? undefined, true);
                        }}
                        disabled={manualAlcoValue === '' || isNaN(Number(manualAlcoValue))}
                        style={{ width: "100%", height: 72, borderRadius: 18, border: "none", background: manualAlcoValue !== '' ? "linear-gradient(145deg,#059669,#047857)" : "#e2e8f0", color: manualAlcoValue !== '' ? "white" : "#94a3b8", fontSize: 18, fontWeight: 800, cursor: manualAlcoValue !== '' ? "pointer" : "not-allowed", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginTop: 16, boxShadow: manualAlcoValue !== '' ? "0 8px 24px rgba(5,150,105,0.4)" : "none" }}>
                        この値で業務開始する
                      </button>
                      <button onClick={() => { manualAlcoInputRef.current = false; setManualAlcoInput(false); }} style={{ width: "100%", height: 52, borderRadius: 14, border: "1.5px solid #e4e9f0", background: "white", color: "#5f6b7c", fontSize: 15, fontWeight: 600, cursor: "pointer", marginTop: 0 }}>
                        ← 戻る
                      </button>
                    </div>
                  </>
                )}
                {alcoholPhase === "done" && alcoholValue !== null && (() => {
                  // 3段階判定。閾値は会社ごとに設定できるため、アラート設定の値を使う。
                  //   正常: 警告閾値未満 ／ 警告: 警告閾値以上・危険閾値未満（要上長確認）
                  //   超過: 危険閾値以上（法定基準値・出勤NG）
                  const th = getAlcoholThresholds();
                  const judgement = judgeAlcohol(alcoholValue, th);
                  const isNormal  = judgement === "normal";
                  const isWarning = judgement === "warning";
                  const isFail    = judgement === "critical";
                  return (
                    <>
                      {/* アイコン */}
                      <div style={{
                        width: 140, height: 140, borderRadius: "50%",
                        background: isNormal ? "linear-gradient(145deg, #10b981, #059669)"
                                  : isWarning ? "linear-gradient(145deg, #f59e0b, #d97706)"
                                  : "linear-gradient(145deg, #ef4444, #dc2626)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        boxShadow: isNormal ? "0 0 0 14px rgba(16,185,129,0.12), 0 16px 48px rgba(16,185,129,0.4)"
                                 : isWarning ? "0 0 0 14px rgba(245,158,11,0.12), 0 16px 48px rgba(245,158,11,0.4)"
                                 : "0 0 0 14px rgba(239,68,68,0.12), 0 16px 48px rgba(239,68,68,0.4)",
                        flexShrink: 0
                      }}>
                        {isNormal
                          ? <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                          : isWarning
                          ? <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                          : <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
                        }
                      </div>

                      {/* タイトル */}
                      <p style={{ fontSize: 20, fontWeight: 800, color: "#0f172a", margin: 0, textAlign: "center" }}>
                        {isNormal ? "検査完了" : isWarning ? "アルコール検知" : "業務開始不可"}
                      </p>

                      {/* 測定値バッジ */}
                      <div style={{
                        background: isNormal ? "#ecfdf5" : isWarning ? "#fffbeb" : "#fef2f2",
                        border: `1px solid ${isNormal ? "#a7f3d0" : isWarning ? "#fde68a" : "#fecaca"}`,
                        borderRadius: 12, padding: "10px 24px", textAlign: "center", width: "100%"
                      }}>
                        <div style={{ fontSize: 20, fontWeight: 800, color: isNormal ? "#059669" : isWarning ? "#d97706" : "#dc2626", marginBottom: 4 }}>
                          {alcoholValue.toFixed(3)} mg/L
                        </div>
                        <div style={{ fontSize: 12, color: "#64748b" }}>
                          {isNormal  && "基準値未満（正常）"}
                          {isWarning && `要上長確認（${fmtMgL(th.critical_mgl)}未満）`}
                          {isFail    && `法定基準値（${fmtMgL(th.critical_mgl)}）超過`}
                        </div>
                      </div>

                      {/* メッセージ */}
                      {isNormal && (
                        <p style={{ fontSize: 13, color: "#94a3b8", margin: 0, textAlign: "center" }}>{stamping ? "打刻中..." : "業務を開始しました"}</p>
                      )}
                      {isWarning && (
                        <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, padding: "12px 16px", width: "100%", textAlign: "center" }}>
                          <p style={{ fontSize: 13, color: "#92400e", margin: 0, fontWeight: 600 }}>
                            ⚠️ 微量のアルコールが検知されました。<br />上長に報告し、確認を受けてください。
                          </p>
                          <p style={{ fontSize: 11, color: "#92400e", margin: "6px 0 0" }}>
                            {stamping ? "打刻中..." : "打刻しました（要確認記録）"}
                          </p>
                        </div>
                      )}
                      {isFail && (
                        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "12px 16px", width: "100%", textAlign: "center" }}>
                          <p style={{ fontSize: 13, color: "#991b1b", margin: 0, fontWeight: 700 }}>
                            🚫 法定基準値（{fmtMgL(th.critical_mgl)}）を超えています。<br />本日の業務開始はできません。
                          </p>
                          <p style={{ fontSize: 11, color: "#991b1b", margin: "6px 0 0" }}>
                            上長・管理者に連絡してください。
                          </p>
                          <button
                            onClick={cancelAlcohol}
                            style={{ marginTop: 10, width: "100%", height: 52, borderRadius: 14, border: "1.5px solid #e4e9f0", background: "white", color: "#5f6b7c", fontSize: 15, fontWeight: 600, cursor: "pointer" }}
                          >
                            閉じる
                          </button>
                        </div>
                      )}

                      {/* 撮影された顔写真（あれば表示） */}
                      {capturedPhoto && (
                        <div style={{ display: "flex", alignItems: "center", gap: 10, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 12, padding: "8px 14px", width: "100%" }}>
                          <img src={capturedPhoto} alt="本人確認写真" style={{ width: 48, height: 48, borderRadius: "50%", objectFit: "cover", transform: "scaleX(-1)", border: "2px solid #e2e8f0" }} />
                          <div style={{ fontSize: 12, color: "#64748b" }}>
                            <div style={{ fontWeight: 700, color: "#0f172a" }}>本人確認写真</div>
                            <div>測定時に自動撮影済み</div>
                          </div>
                        </div>
                      )}

                      {/* 出勤完了後: 次の人 / 続けるボタン */}
                      {!stamping && !isFail && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 10, width: "100%" }}>
                          <button
                            onClick={() => { cancelAlcohol(); setCapturedPhoto(null); capturedPhotoRef.current = null; onLogout(); }}
                            style={{ width: "100%", height: 72, borderRadius: 18, border: "none", background: "linear-gradient(145deg,#0057d9,#0369a1)", color: "white", fontSize: 18, fontWeight: 800, cursor: "pointer", boxShadow: "0 8px 24px rgba(0,87,217,0.4)", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginTop: 0 }}
                          >
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                            次の方はこちら
                          </button>
                          <button
                            onClick={() => { cancelAlcohol(); setCapturedPhoto(null); capturedPhotoRef.current = null; }}
                            style={{ width: "100%", height: 52, borderRadius: 14, border: "1.5px solid #e4e9f0", background: "white", color: "#5f6b7c", fontSize: 15, fontWeight: 600, cursor: "pointer", marginTop: 0 }}
                          >
                            このまま続ける
                          </button>
                          <button
                            onClick={startAlcohol}
                            style={{ background: "none", border: "none", color: "#94a3b8", fontSize: 13, cursor: "pointer", padding: "10px", display: "block", width: "100%", textAlign: "center" }}
                          >
                            測定をやり直す
                          </button>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}

            {/* カメラ撮影UI（出勤前の本人確認） */}
            {cameraStep !== 'idle' && !alcoholActive && workSelectStep === 'none' && (
              <div className="tablet-wide" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 20, width: "100%", maxWidth: 340, background: "white", borderRadius: 20, border: "1px solid #e4e9f0", padding: "24px 20px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
                {/* セクションヘッダー */}
                <div style={{ width: 52, height: 52, borderRadius: "50%", background: "linear-gradient(135deg,#0057d9,#38bdf8)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 0" }}>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
                </div>
                {cameraStep === 'preview' && (
                  <>
                    <p style={{ fontSize: 20, fontWeight: 800, color: "#0f172a", margin: 0, textAlign: "center" }}>本人確認撮影</p>
                    <p style={{ fontSize: 13, color: "#64748b", margin: 0, textAlign: "center" }}>
                      カメラに顔を向けてシャッターを押してください
                    </p>
                    <div style={{ width: "100%", borderRadius: 16, overflow: "hidden", background: "#000", aspectRatio: "4/3", position: "relative" }}>
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        style={{ width: "100%", height: "100%", objectFit: "cover", transform: "scaleX(-1)", display: "block" }}
                      />
                    </div>
                    <button
                      onClick={capturePhoto}
                      style={{ width: 80, height: 80, borderRadius: "50%", border: "4px solid white", background: "linear-gradient(145deg,#059669,#047857)", color: "white", fontSize: 30, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 0 0 6px rgba(5,150,105,0.3), 0 8px 24px rgba(5,150,105,0.4)" }}
                    >
                      <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
                    </button>
                    <button
                      onClick={skipCamera}
                      style={{ background: "none", border: "none", color: "#94a3b8", fontSize: 13, cursor: "pointer", padding: "10px", display: "block", width: "100%", textAlign: "center" }}
                    >
                      カメラをスキップして業務開始
                    </button>
                  </>
                )}
                {cameraStep === 'captured' && capturedPhoto && (
                  <>
                    <p style={{ fontSize: 20, fontWeight: 800, color: "#0f172a", margin: 0, textAlign: "center" }}>撮影完了</p>
                    <p style={{ fontSize: 13, color: "#64748b", margin: 0, textAlign: "center" }}>
                      この写真でよければ「業務開始」を押してください
                    </p>
                    <div style={{ width: "100%", borderRadius: 16, overflow: "hidden", border: "3px solid #10b981", aspectRatio: "4/3", position: "relative" }}>
                      <img
                        src={capturedPhoto}
                        alt="撮影した写真"
                        style={{ width: "100%", height: "100%", objectFit: "cover", transform: "scaleX(-1)", display: "block" }}
                      />
                    </div>
                    <button
                      onClick={proceedFromCamera}
                      style={{ width: "100%", height: 72, borderRadius: 18, border: "none", background: "linear-gradient(145deg,#059669,#047857)", color: "white", fontSize: 18, fontWeight: 800, cursor: "pointer", boxShadow: "0 8px 24px rgba(5,150,105,0.4)", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginTop: 0 }}
                    >
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      {pendingAlcoholRequiredRef.current ? "この写真でアルコール検査へ" : "この写真で業務開始"}
                    </button>
                    <button
                      onClick={retakePhoto}
                      style={{ width: "100%", height: 52, borderRadius: 14, border: "1.5px solid #e4e9f0", background: "white", color: "#5f6b7c", fontSize: 15, fontWeight: 600, cursor: "pointer", marginTop: 0 }}
                    >
                      撮り直す
                    </button>
                    <button
                      onClick={skipCamera}
                      style={{ background: "none", border: "none", color: "#94a3b8", fontSize: 13, cursor: "pointer", padding: "10px", display: "block", width: "100%", textAlign: "center" }}
                    >
                      写真なしで業務開始する
                    </button>
                  </>
                )}
              </div>
            )}

            {/* 通常打刻ボタン */}
            {!alcoholActive && clockOutStep === "none" && cameraStep === 'idle' && workSelectStep === 'none' && (
              <div ref={mainActionRef} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16, width: "100%", maxWidth: 320, scrollMarginTop: 8 }}>
                {(sessionStatus === "idle" || sessionStatus === "resting") && (
                  <button
                    onClick={() => {
                      if (stamping) return;
                      if (needBoardFirst) { showToast("先に乗船登録をしてください（上の「乗船登録」ボタン）", "error"); return; }
                      if (!selectedVesselId) { showToast("管理者に船舶の割り当てを依頼してください", "error"); return; }
                      openWorkSelect("clock_in");
                    }}
                    style={{
                      width: "100%", height: 120, borderRadius: 26, border: "none",
                      background: stamping || needBoardFirst ? "#e4e9f0" : "linear-gradient(145deg, #059669, #047857)",
                      color: stamping || needBoardFirst ? "#94a3b8" : "white",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 16,
                      cursor: stamping ? "not-allowed" : "pointer",
                      boxShadow: stamping || needBoardFirst ? "none" : "0 10px 36px rgba(5,150,105,0.45)",
                      transition: "all 0.15s",
                    }}
                    onPointerDown={e => { if (!stamping) (e.currentTarget as HTMLButtonElement).style.transform = "scale(0.97)"; }}
                    onPointerUp={e => { (e.currentTarget as HTMLButtonElement).style.transform = "scale(1)"; }}
                  >
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                    <span style={{ fontSize: 28, fontWeight: 900, letterSpacing: "0.01em" }}>{stamping ? "処理中..." : "業務開始"}</span>
                  </button>
                )}

                {sessionStatus === "working" && (
                  <button
                    onClick={() => { if (stamping) return; prefillClockOutTypes(); setClockOutStep("select"); }}
                    disabled={stamping}
                    style={{
                      width: "100%", height: 120, borderRadius: 26, border: "none",
                      background: stamping ? "#e4e9f0" : "linear-gradient(145deg, #ef4444, #dc2626)",
                      color: stamping ? "#94a3b8" : "white",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 16,
                      cursor: stamping ? "not-allowed" : "pointer",
                      boxShadow: stamping ? "none" : "0 10px 36px rgba(239,68,68,0.45)",
                      transition: "all 0.15s",
                    }}
                    onPointerDown={e => { if (!stamping) (e.currentTarget as HTMLButtonElement).style.transform = "scale(0.97)"; }}
                    onPointerUp={e => { (e.currentTarget as HTMLButtonElement).style.transform = "scale(1)"; }}
                  >
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                    <span style={{ fontSize: 28, fontWeight: 900 }}>{stamping ? "処理中..." : "業務終了"}</span>
                  </button>
                )}
                {(sessionStatus === "working" || sessionStatus === "resting") && (
                  <button
                    onClick={() => {
                      if (!currentSession?.vessel_id && !selectedVesselId) return;
                      setActivityModalOpen(true);
                    }}
                    style={{ width: "100%", padding: "14px 0", borderRadius: 16, border: "1.5px solid #c4b5fd", background: "#f5f3ff", color: "#7c3aed", fontSize: 15, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14"/></svg>
                    {hasFeature('unified_work_types') ? '作業を追加' : '臨時作業を申請'}
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {clockOutStep === "none" && !isPersonalDevice() && (() => {
        let perms: Record<string, boolean> = {};
        try { perms = JSON.parse(localStorage.getItem("catms_permissions") ?? "{}"); } catch { /* noop */ }
        // 打刻画面の下部メニュー。縦一列だと渋滞するため2列のコンパクトなタイルにまとめる。
        const actions: { emoji: string; label: string; path: string; accent?: boolean }[] = [
          { emoji: "📋", label: "勤怠を確認・編集", path: "/history" },
          { emoji: "⚠️", label: "ヒヤリハット報告", path: "/safety-report" },
          { emoji: "🌴", label: "休暇・希望休", path: "/leave" },
        ];
        // アルコール測定のみ（打刻と切り離して測定だけ記録する。測定連動の勤怠 alcohol_linked_attendance の会社のみ）
        if (hasFeature('alcohol_linked_attendance')) actions.unshift({ emoji: "🧪", label: "アルコール測定のみ", path: "/alcohol-check", accent: true });
        // 乗下船表（自分の船の乗/休カレンダー）。会社ごとの機能フラグで出し分ける
        if (hasFeature('crew_boarding_sheet')) actions.push({ emoji: "🗓", label: "乗下船表", path: "/boarding-sheet" });
        // 社内様式（船用品注文書・船員旅費精算書）は書類権限が無い船員も作れる
        if (perms.admin_documents || hasFeature("extra_cert_forms")) actions.push({ emoji: "📝", label: "証明書作成", path: "/cert-create", accent: true });
        if (perms.print_documents) actions.push({ emoji: "📄", label: "書類・印刷", path: "/documents" });
        // カツオポータル（機能フラグ portal の会社のみ）
        if (hasFeature('portal')) {
          actions.push({ emoji: "📢", label: "掲示板・回覧", path: "/portal-board" });
          actions.push({ emoji: "📘", label: "マニュアル", path: "/portal-manuals" });
          actions.push({ emoji: "🗨️", label: "チャット", path: "/portal-chat" });
          actions.push({ emoji: "📨", label: "各種申請", path: "/portal-requests" });
        }
        return (
          <div style={{ padding: "8px 16px 12px", flexShrink: 0 }}>
            {/* ポータルの今（未読・最新お知らせ）。フラグ portal の会社のみ */}
            {hasFeature('portal') && <div style={{ marginBottom: 8 }}><PortalSummaryCard /></div>}
            {/* 見出しを兼ねた開閉ボタン。閉じているときはこの1行だけになるので、
                打刻ボタンに画面を譲れる。 */}
            <button
              onClick={toggleHomeMenu}
              aria-expanded={homeMenuOpen}
              style={{
                width: "100%", padding: "10px 12px", borderRadius: 12,
                border: "1.5px solid #e4e9f0", background: "#fff", color: "#3d5166",
                fontSize: 13, fontWeight: 700, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                marginBottom: homeMenuOpen ? 8 : 0,
              }}
            >
              {homeMenuOpen ? "その他の機能を閉じる" : `その他の機能（${actions.length}件）`}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
                style={{ transform: homeMenuOpen ? "rotate(180deg)" : undefined, transition: "transform 0.2s" }}>
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
            {homeMenuOpen && (
            <div className="tablet-tiles" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {actions.map((a, i) => {
                // 奇数個のときは最後の1つを横幅いっぱいにして余白を作らない
                const spanFull = actions.length % 2 === 1 && i === actions.length - 1;
                return (
                  <button key={a.path} onClick={() => nav(a.path)}
                    style={{
                      gridColumn: spanFull ? "1 / -1" : undefined,
                      padding: spanFull ? "12px" : "12px 6px", borderRadius: 14,
                      border: a.accent ? "1.5px solid #c7d2fe" : "1.5px solid #e4e9f0",
                      background: a.accent ? "#eef2ff" : "#fff",
                      color: a.accent ? "#4338ca" : "#3d5166",
                      fontSize: 13, fontWeight: 700, cursor: "pointer",
                      display: "flex", flexDirection: spanFull ? "row" : "column",
                      alignItems: "center", justifyContent: "center", gap: spanFull ? 8 : 6,
                      minHeight: spanFull ? 0 : 60, textAlign: "center", lineHeight: 1.25,
                    }}>
                    <span style={{ fontSize: 20, lineHeight: 1 }}>{a.emoji}</span>
                    <span>{a.label}</span>
                  </button>
                );
              })}
            </div>
            )}
          </div>
        );
      })()}

      <BottomNav />
      {activityModalOpen && currentSession && (
        <CrewActivityModal
          sessionId={currentSession.id}
          vesselId={currentSession.vessel_id ?? selectedVesselId}
          onClose={() => setActivityModalOpen(false)}
        />
      )}
    </div>
  );
};
